import React, { useState, useRef, useEffect } from "react";
import {
  AreaChart,
  Area,
  BarChart,
  Bar,
  LineChart,
  Line,
  ComposedChart,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Cell,
  ReferenceLine,
  ReferenceArea,
  Legend,
} from "recharts";
import {
  RouteIcon,
  TrendingUp,
  Users,
  Activity,
  Crosshair,
  Sparkles,
  Trophy,
  ClipboardList,
  NotebookPen,
  Plus,
  Pencil,
  Trash2,
  ChevronDown,
  ChevronUp,
  Search,
  ArrowUpRight,
  ArrowDownRight,
  Download,
  Calendar,
  Clock,
  CheckCircle2,
  XCircle,
  AlertTriangle,
  Info,
  Zap,
  Brain,
  BarChart3,
  X,
  UserCheck,
  Building2,
  HeartHandshake,
  CalendarDays,
  CalendarCheck,
  Stethoscope,
  FlaskConical,
  Headphones,
  Receipt,
  Smartphone,
  Globe,
  HeartPulse,
  MessageSquare,
  ShoppingCart,
  CircleAlert,
  Lightbulb,
  TrendingDown,
  ScanSearch,
  Filter,
  Bell,
  Send,
  PanelLeftClose,
  PanelLeft,
  Archive,
  Link as LinkIcon,
  ExternalLink,
  History,
  Heart,
  MessageCircle,
  Share2,
  Upload,
  MapPin,
  ThumbsUp,
  ThumbsDown,
  EyeOff,
  Eye,
  Phone,
  Mail,
} from "lucide-react";

import Logo from "../../assets/img/logo.png";

// Shared tokens / helpers / sub-components (sliced out of v45 monolith)
import * as S from "./_shared";

// 10 sliced views (one per tab)
import ExecutiveView from "./ExecutiveView";
import JourneyView from "./JourneyView";
import TrendsView from "./TrendsView";
import SegmentationView from "./SegmentationView";
import DriversView from "./DriversView";
import EvaView from "./EvaView";
import BenchmarkingView from "./BenchmarkingView";
import ActionPlanView from "./ActionPlanView";
import CooperacaoView from "./CooperacaoView";
import VerbatinsView from "./VerbatinsView"; // repurposed as Metodologia (quest)

// Mocks — reassembled at runtime into the v45 DATA shape so internal code
// (e.g. `DATA[periodoG].etapas`) stays unchanged.
import overviewMock from "../../mocks/overview.json";
import etapasMock from "../../mocks/etapas.json";
import tendenciasMock from "../../mocks/tendencias.json";
import driversMock from "../../mocks/drivers.json";
import segmentacaoMock from "../../mocks/segmentacao.json";
import benchmarkingMock from "../../mocks/benchmarking.json";

// Pull every shared export into local scope so the Dashboard body (state hooks,
// modals, TopBar, TabNav, NPSAnchor, etc.) can use them unqualified.
const {
  ACOES_INIT,
  AcaoCard,
  AcaoDetalhe,
  AcaoModal,
  C,
  CES_COLS,
  CES_ETAPAS_NAMES,
  CES_TREND,
  CIDADES_COBERTURA,
  COOP_PLANOS_MOCK,
  CSAT_COLS,
  CSAT_ETAPAS_NAMES,
  CSAT_TREND,
  Card,
  DRIVERS_CANONICOS,
  DRIVER_TO_PILLAR,
  DistBar,
  ESCALA,
  ETAPA_DRIVER_AFFINITY,
  ETAPA_HIST,
  EtCard,
  EvaChat,
  EvaChatHistory,
  EvaInsights,
  F,
  HIST_ACOES_POR_ETAPA,
  JCFG,
  JICONS,
  LOGO,
  MESES_HIST,
  META_NPS,
  MUDANCAS_DESDE_ULTIMA_VISITA,
  MiniSparkline,
  NPSAnchor,
  PC,
  PDFExportModal,
  PER,
  PER_DEFAULT_LABELS,
  PER_OPTIONS,
  PILLAR_COLORS,
  PeriodPill,
  PeriodPillBig,
  SC,
  SHORT,
  SUCESSO_BADGES,
  SUCESSO_LEVELS,
  SYS_NPS,
  Section,
  TREND_BY_AGRUP,
  TT,
  Tag,
  ULTIMA_VISITA,
  UNIMED,
  VERBATINS_POR_ETAPA,
  Z_CX,
  Zonas,
  _META_CES_TR,
  _META_CSAT_TR,
  _META_NPS_TR,
  _SYS_CES_TR,
  _SYS_CSAT_TR,
  _SYS_NPS_TR,
  diasRest,
  etSt,
  exportPDF,
  fmtDt,
  getDriverInfo,
  getDriversForEtapa,
  getPilarOf,
  getSucessoStats,
  moe,
  pctChk,
  safeGanhoPp,
  tempoRelativo,
  todayStr,
} = S;

// Reassemble DATA from the 6 thematic mocks across 4 periods.
const PERIODS = ["mensal", "trimestral", "semestral", "anual"];
const DATA = Object.fromEntries(
  PERIODS.map((p) => [
    p,
    {
      ...overviewMock[p],
      ...etapasMock[p],
      ...tendenciasMock[p],
      ...driversMock[p],
      ...segmentacaoMock[p],
      ...benchmarkingMock[p],
    },
  ]),
);

export default function Dashboard() {
  const [periodoG, setPeriodoG] = useState("trimestral");
  const [cidadesAtivas, setCidadesAtivas] = useState(CIDADES_COBERTURA); // todas selecionadas por padrão
  const [tipoBeneficiario, setTipoBeneficiario] = useState("todos"); // "todos" | "proprio" | "intercambio"
  const [tipoBenefAberto, setTipoBenefAberto] = useState(false);
  const [sucessoModalOpen, setSucessoModalOpen] = useState(false);
  const [sucessoFiltroPeriodo, setSucessoFiltroPeriodo] = useState("todos"); // todos|3m|6m|12m
  const [filtroCidadeAberto, setFiltroCidadeAberto] = useState(false);
  const toggleCidade = (c) =>
    setCidadesAtivas((prev) =>
      prev.includes(c) ? prev.filter((x) => x !== c) : [...prev, c],
    );
  const todasCidades = cidadesAtivas.length === CIDADES_COBERTURA.length;
  const nenhumaCidade = cidadesAtivas.length === 0;
  const [periodoCustom, setPeriodoCustom] = useState({ inicio: "", fim: "" });
  // Notificações (mensagens da Hazo.ai pro cliente)
  const [notifOpen, setNotifOpen] = useState(false);
  const [notifs, setNotifs] = useState([
    {
      id: 10,
      tipo: "briefing",
      titulo: "✨ Briefing Eva — Março 2026",
      corpo:
        "O NPS Geral fechou Março em 58 pts (−2 vs Fev), puxado pelo peso de Atend. Operadora em 18% e Agend. Consultas em 34%. Aplicativo voltou a subir (+4pp) e Consulta segue estável em 82%. 214 respostas coletadas, confiança alta (erro ≤ 2,8pp). Pontos de atenção: Reembolso aparece como driver de atrito dominante (−52 net). Recomendação prioritária: acelerar credenciamento de especialistas e revisar SLA de 1º contato no 0800.",
      quando: "há 1h",
      lida: false,
      tempoLeitura: "30s",
    },
    {
      id: 11,
      tipo: "eva_plano",
      titulo: "💡 Eva sugere plano de ação: Atend. Operadora",
      corpo:
        "Detectei que Atend. Operadora está em 18% há 3 meses consecutivos. Já montei um plano com 4 tarefas, 2 responsáveis e prazo de 90 dias. Clique para criar automaticamente.",
      quando: "há 30 min",
      lida: false,
      planoSugerido: {
        titulo: "Redesenhar fluxo de atendimento 0800",
        desc: "Dar autonomia ampliada ao front-line + SLA de resolução no 1º contato. Baseado no padrão detectado pela Eva: 71% das respostas negativas mencionam falta de resolução.",
        etapa: "Atend. Operadora",
        tipo: "ces",
        v_atual: 18,
        v_meta: 65,
        resp: "Gerência de CX",
        prazo: "2026-07-07",
        prio: "alta",
        origem: "Eva — Gerador automático",
        drivers: ["resolutividade", "agilidade", "burocracia"],
        check: [
          {
            t: "Workshop de processo com equipe do 0800 (identificar gargalos)",
            diasDoInicio: 0,
            duracaoDias: 7,
          },
          {
            t: "Redesenhar fluxograma com autonomia de decisão por nível",
            diasDoInicio: 7,
            duracaoDias: 14,
          },
          {
            t: "Treinar toda equipe no novo protocolo (2 turmas)",
            diasDoInicio: 21,
            duracaoDias: 21,
          },
          {
            t: "Medir CES pós-implantação e comparar baseline",
            diasDoInicio: 42,
            duracaoDias: 30,
          },
        ],
      },
    },
    {
      id: 0,
      tipo: "conquista",
      titulo: "🎉 Você subiu para nível Prata!",
      corpo:
        'Seu plano de ação acumulou +16pp em melhorias. Desbloqueou a conquista "Marca dos 10". Continue assim — Ouro está a +14pp.',
      quando: "há 5 min",
      lida: false,
    },
    {
      id: 1,
      tipo: "alerta",
      titulo: "Eva detectou padrão crítico",
      corpo:
        "Atend. Operadora caiu para 18% — Eva sugere plano de ação imediato.",
      quando: "agora",
      lida: false,
    },
    {
      id: 2,
      tipo: "insight",
      titulo: "Nova onda de pesquisa disponível",
      corpo: "214 respostas coletadas nos últimos 7 dias. Mar/26 fechado.",
      quando: "há 2h",
      lida: false,
    },
    {
      id: 3,
      tipo: "plano",
      titulo: "Plano de ação atualizado",
      corpo: '"Redesenhar atendimento 0800" atingiu 50% de progresso.',
      quando: "há 1d",
      lida: true,
    },
    {
      id: 4,
      tipo: "comunicado",
      titulo: "Workshop trimestral CX",
      corpo:
        "Próxima sessão agendada para 15/04. Confirme presença com a equipe Hazo.ai.",
      quando: "há 3d",
      lida: true,
    },
  ]);
  // Notifs ocultadas pelo usuário (filtradas em toda renderização)
  const notifsVisiveis = notifs.filter((n) => !n.oculta);
  const notifsOcultas = notifs.filter((n) => n.oculta);
  const naoLidas = notifsVisiveis.filter((n) => !n.lida).length;
  const marcarComoLida = (id) =>
    setNotifs((prev) =>
      prev.map((n) => (n.id === id ? { ...n, lida: true } : n)),
    );
  const marcarTodasLidas = () =>
    setNotifs((prev) => prev.map((n) => ({ ...n, lida: true })));
  // Feedback inline em cada notif (toggle)
  const setNotifFeedback = (id, valor) =>
    setNotifs((prev) =>
      prev.map((n) => {
        if (n.id !== id) return n;
        return { ...n, feedback: n.feedback === valor ? null : valor };
      }),
    );
  const ocultarNotif = (id) =>
    setNotifs((prev) =>
      prev.map((n) => (n.id === id ? { ...n, oculta: true } : n)),
    );
  const desocultarNotif = (id) =>
    setNotifs((prev) =>
      prev.map((n) => (n.id === id ? { ...n, oculta: false } : n)),
    );
  // Toggle da seção de ocultos no dropdown
  const [mostrarOcultos, setMostrarOcultos] = useState(false);
  // Modal fullscreen "Ver todas"
  const [notifModalOpen, setNotifModalOpen] = useState(false);
  const [notifModalFiltro, setNotifModalFiltro] = useState("todas"); // todas | nao_lidas | uteis

  // ─── Eva Chat — Histórico de conversas (estilo ChatGPT/Claude) ─────────────
  const SAUDACAO_EVA = {
    role: "assistant",
    content:
      "Oi! Eu sou a Eva 👋, uma persona criada a partir de entrevistas reais para representar o seu cliente, traduzindo de forma humanizada suas experiências, expectativas e percepções. O que você quer saber sobre a minha experiência?",
  };
  const _agora = Date.now();
  const _h = 3600000;
  const [evaChats, setEvaChats] = useState([
    {
      id: "c1",
      titulo: "Nova conversa",
      criadoEm: _agora,
      mensagens: [SAUDACAO_EVA],
    },
    {
      id: "c2",
      titulo: "Por que o atendimento é tão difícil?",
      criadoEm: _agora - 2 * _h,
      mensagens: [
        SAUDACAO_EVA,
        { role: "user", content: "Por que o atendimento é tão difícil?" },
        {
          role: "assistant",
          content:
            "Cara, o pior é quando preciso resolver alguma coisa na operadora. Liguei outro dia pra autorizar um exame, fiquei 22 minutos na fila, fui transferida 3 vezes, e no final me pediram pra mandar o pedido por email — sendo que o atendente já tinha o pedido no sistema. Parece que cada um quer empurrar pro outro. Eu só queria resolver meu problema sem precisar repetir minha história 4 vezes.",
        },
        { role: "user", content: "Isso acontece sempre?" },
        {
          role: "assistant",
          content:
            "Quase sempre que envolve autorização ou reembolso, sim. Pra coisas simples tipo confirmar uma consulta ou pegar a carteirinha, é mais tranquilo. Mas o problema é que justamente quando eu mais preciso da operadora — quando tem alguma coisa urgente — é aí que ela mais falha. Isso me faz ficar com uma sensação de que eu pago bem e na hora H não tem ninguém pra me ajudar de verdade.",
        },
      ],
    },
    {
      id: "c3",
      titulo: "Análise dos drivers de churn",
      criadoEm: _agora - 26 * _h,
      mensagens: [
        SAUDACAO_EVA,
        {
          role: "user",
          content: "Quais drivers mais levam à decisão de cancelar o plano?",
        },
        {
          role: "assistant",
          content:
            "Olha, da minha experiência e do que escuto de gente próxima, são 3 coisas principais: 1) Reajuste anual sem aviso claro — quando chega o boleto 30% mais caro do nada, dá uma raiva enorme. 2) Negativa de cobertura em momento crítico — tipo precisar de exame e receber 'não está coberto'. 3) Burocracia infinita pra reembolso — eu já pensei seriamente em cancelar quando demorou 47 dias pra receber R$ 800 que era meu por direito.",
        },
        { role: "user", content: "E o que poderia segurar essas pessoas?" },
        {
          role: "assistant",
          content:
            "Pra falar a verdade, na hora que a pessoa decidiu cancelar, é muito difícil reverter. O ponto é evitar chegar nesse estado. O que faria diferença pra mim: comunicação proativa antes do reajuste explicando porquê, transparência total nas regras de cobertura, e prazo de reembolso real (mesmo que seja 30 dias) cumprido sempre. A confiança quebra muito mais pela falta de previsibilidade do que pelo problema em si.",
        },
      ],
    },
    {
      id: "c4",
      titulo: "Comparar Q1 2026 vs Q4 2025",
      criadoEm: _agora - 3 * 24 * _h,
      mensagens: [
        SAUDACAO_EVA,
        {
          role: "user",
          content: "Como você vê a evolução do Q4/25 pro Q1/26?",
        },
        {
          role: "assistant",
          content:
            "Eu sinto que melhorou em algumas coisas. O aplicativo ficou mais rápido, agora consigo ver minha carteirinha sem demora. As consultas com meu médico de família continuam ótimas — isso é o que mais me faz ficar no plano. Mas o Atend. Operadora continua igualzinho ruim. Esse é o ponto que me incomoda mais e parece que não muda nunca, sabe?",
        },
      ],
    },
    {
      id: "c5",
      titulo: "Como melhorar o app?",
      criadoEm: _agora - 7 * 24 * _h,
      mensagens: [
        SAUDACAO_EVA,
        { role: "user", content: "O que você acha que falta no aplicativo?" },
        {
          role: "assistant",
          content:
            "O app melhorou muito esse último ano. Hoje consigo: ver carteirinha, consultar rede credenciada, ver histórico de consultas. Mas ainda falta: agendamento direto pelo app (hoje preciso ligar), acompanhar status de reembolso em tempo real (ficar adivinhando se já caiu), e poder iniciar uma autorização sem ter que ligar pra central. Se eu pudesse fazer essas 3 coisas pelo app, eu praticamente não precisava nem ligar pra operadora.",
        },
      ],
    },
  ]);
  const [evaChatAtivoId, setEvaChatAtivoId] = useState("c1");
  const [historicoEvaVisivel, setHistoricoEvaVisivel] = useState(true);
  // Gera título a partir da primeira pergunta do usuário (truncado)
  const gerarTituloChat = (mensagens) => {
    const primeiraDoUser = mensagens.find((m) => m.role === "user");
    if (!primeiraDoUser) return "Nova conversa";
    const t = primeiraDoUser.content.trim();
    return t.length > 48 ? t.slice(0, 45) + "..." : t;
  };
  const novoChatEva = () => {
    const novoId = "c" + Date.now();
    setEvaChats((prev) => [
      {
        id: novoId,
        titulo: "Nova conversa",
        criadoEm: Date.now(),
        mensagens: [SAUDACAO_EVA],
      },
      ...prev,
    ]);
    setEvaChatAtivoId(novoId);
  };
  const selecionarChatEva = (id) => setEvaChatAtivoId(id);
  const excluirChatEva = (id) => {
    setEvaChats((prev) => {
      const novos = prev.filter((c) => c.id !== id);
      // Se excluiu o ativo, seleciona o primeiro da lista
      if (id === evaChatAtivoId && novos.length > 0) {
        setEvaChatAtivoId(novos[0].id);
      }
      return novos;
    });
  };
  const atualizarMensagensChatEva = (novasMsgs) => {
    setEvaChats((prev) =>
      prev.map((c) => {
        if (c.id !== evaChatAtivoId) return c;
        // Auto-rename: se ainda é "Nova conversa" e já tem pergunta do user, gera título
        const novoTitulo =
          c.titulo === "Nova conversa" ? gerarTituloChat(novasMsgs) : c.titulo;
        return { ...c, mensagens: novasMsgs, titulo: novoTitulo };
      }),
    );
  };

  const [metas, setMetas] = useState({ nps: 70, csat: 79, ces: 66, jcx: 73 });
  const [editandoMeta, setEditandoMeta] = useState(null);
  // Metas individuais das 11 etapas — inicializadas a partir de JCFG
  const [metasEtapas, setMetasEtapas] = useState(() => {
    const init = {};
    Object.keys(JCFG).forEach((k) => {
      init[k] = JCFG[k].meta;
    });
    return init;
  });
  const [editandoMetaEt, setEditandoMetaEt] = useState(null);
  // Minimizar card inteiro de metas
  const [metasCardOpen, setMetasCardOpen] = useState(true);
  // Toggle de gamificação no card de Sucessos (on = full com níveis/badges, off = modo simples corporativo)
  const [gamificacaoOn, setGamificacaoOn] = useState(true);
  // Feed cooperativo Sistema Unimed
  const [coopPlanos, setCoopPlanos] = useState(COOP_PLANOS_MOCK);
  const [coopBusca, setCoopBusca] = useState("");
  const [coopEtapaFilter, setCoopEtapaFilter] = useState("todas");
  const [coopCurtidas, setCoopCurtidas] = useState(() => new Set());
  const [coopDriverFilter, setCoopDriverFilter] = useState(() => new Set());
  const [coopComentariosAbertos, setCoopComentariosAbertos] = useState(
    () => new Set(),
  );
  const [coopNovoComentario, setCoopNovoComentario] = useState({});
  // Wizard de conclusão automática (trigger: status → concluido sem sucesso registrado)
  const [concluirWizard, setConcluirWizard] = useState(null);
  // Bulk adjust de metas (% sobre o atual)
  const [bulkOpen, setBulkOpen] = useState(false);
  const [bulkPct, setBulkPct] = useState("");
  const [bulkApplyGerais, setBulkApplyGerais] = useState(true);
  const [bulkApplyEtapas, setBulkApplyEtapas] = useState(true);
  // Helper: atualiza state E muta JCFG (pra propagar pra todas as referências sem refatorar)
  const setMetaEtapa = (nome, valor) => {
    JCFG[nome].meta = valor;
    setMetasEtapas((prev) => ({ ...prev, [nome]: valor }));
  };
  const [tab, setTab] = useState("executivo");
  const [etapaSel, setEtapaSel] = useState(null);
  const [acoes, setAcoes] = useState(ACOES_INIT);
  const [acaoSel, setAcaoSel] = useState(null);
  const [hiddenLines, setHiddenLines] = useState({});
  const toggleLine = (key) =>
    setHiddenLines((prev) => ({ ...prev, [key]: !prev[key] }));
  const [filtroSt, setFiltroSt] = useState(() => new Set());
  const [filtroResp, setFiltroResp] = useState(() => new Set());
  const [stDropOpen, setStDropOpen] = useState(false);
  const [respDropOpen, setRespDropOpen] = useState(false);
  const [busca, setBusca] = useState("");
  const [modal, setModal] = useState(null);
  const [pJ, setPJ] = useState("trimestral");
  const [pT, setPT] = useState("trimestral");
  const [agrupT, setAgrupT] = useState("mes"); // granularidade da aba Tendências: mes/tri/sem/ano
  const [pS, setPS] = useState("trimestral");
  const [pE, setPE] = useState("trimestral");
  const [pD, setPD] = useState("trimestral");
  const [engajamentoModalOpen, setEngajamentoModalOpen] = useState(false);
  const [prefilledAcao, setPrefilledAcao] = useState(null);
  const [verbatinsModal, setVerbatinsModal] = useState(null); // {etapa, drivers: {pos, neg}} ou null
  const [etapaDriversFilter, setEtapaDriversFilter] = useState(null); // null = Geral, ou nome da etapa
  const [etapaHistSel, setEtapaHistSel] = useState("Atend. Operadora"); // etapa para o gráfico de evolução por etapa
  const [driverSortMode, setDriverSortMode] = useState("volume"); // "volume" | "pior" | "melhor"
  const [segIndicador, setSegIndicador] = useState("nps"); // "nps" | "csat" | "ces" — qual indicador a aba Segmentação mostra
  const [segHistMode, setSegHistMode] = useState({}); // toggle de série histórica por chart (key: chartId, value: bool)
  const toggleSegHist = (key) =>
    setSegHistMode((prev) => ({ ...prev, [key]: !prev[key] }));
  const [pB, setPB] = useState("trimestral");

  useEffect(() => {
    setPJ(periodoG);
    setPT(periodoG);
    setPS(periodoG);
    setPE(periodoG);
    setPD(periodoG);
    setPB(periodoG);
  }, [periodoG]);

  function saveAcao(a) {
    // Detecta transição de status → concluido sem sucesso registrado (pra disparar o wizard)
    const old = acoes.find((x) => x.id === a.id);
    const isTransicaoConcluido =
      old &&
      old.status !== "concluido" &&
      a.status === "concluido" &&
      !a.sucesso;
    setAcoes((prev) =>
      prev.some((x) => x.id === a.id)
        ? prev.map((x) => (x.id === a.id ? a : x))
        : [...prev, a],
    );
    if (isTransicaoConcluido) {
      const ganhoAuto =
        (Number(a.v_atual) || 0) -
        (Number(a.v_baseline) || Number(a.v_atual) || 0);
      setConcluirWizard({
        step: "registrar_sucesso",
        acao: a,
        ganhoAuto,
        ganhoEdit: String(Math.max(0, Math.round(ganhoAuto * 10) / 10)),
        obs: "",
        autorPub: a.resp || "Renato · Gerência CX",
        tituloPub: a.titulo,
        descPub: a.desc || "",
        telefonePub: "",
        emailPub: "",
      });
    }
  }
  function delAcao(id) {
    setAcoes((prev) => prev.filter((x) => x.id !== id));
    if (acaoSel === id) setAcaoSel(null);
  }

  // Cria um plano de ação a partir de uma sugestão da Eva (vinda de uma notificação)
  function criarPlanoSugerido(notif) {
    if (!notif.planoSugerido) return;
    const sug = notif.planoSugerido;
    const hoje = new Date();
    const addDias = (d) => {
      const r = new Date(hoje);
      r.setDate(r.getDate() + d);
      return r.toISOString().slice(0, 10);
    };
    // Converte check items com diasDoInicio/duracaoDias pra datas reais
    const check = (sug.check || []).map((c, i) => ({
      i: Date.now() + i,
      t: c.t,
      f: false,
      inicio: addDias(c.diasDoInicio || 0),
      fim: addDias((c.diasDoInicio || 0) + (c.duracaoDias || 7)),
      concluido: null,
    }));
    // Monta ação completa pré-preenchida
    const novaAcao = {
      id: "eva_" + Date.now(),
      titulo: sug.titulo,
      desc: sug.desc,
      etapa: sug.etapa,
      tipo: sug.tipo,
      v_atual: sug.v_atual,
      v_meta: sug.v_meta,
      resp: sug.resp,
      prazo: sug.prazo,
      prio: sug.prio || "alta",
      status: "pendente",
      origem: sug.origem || "Eva — Gerador automático",
      drivers: sug.drivers || [],
      links: [],
      check,
      hist: [
        {
          dt: todayStr(),
          tx: "Plano criado automaticamente pela Eva a partir de alerta crítico",
        },
      ],
      med: [{ p: "Atual", v: sug.v_atual }],
    };
    // Navega pra aba de Plano de Ação, fecha notifs, abre modal pré-preenchido
    setTab("acoes");
    setNotifOpen(false);
    setModal(novaAcao);
    // Marca a notificação como lida
    marcarComoLida(notif.id);
  }

  // Compartilha um plano concluído no feed CooperAção Unimed
  function compartilharPlano(a, opts = {}) {
    if (!a) return;
    const novoPost = {
      id: "coop_" + Date.now(),
      unimed: UNIMED,
      uf: "SP",
      avatar: "🌱",
      verif: true,
      postadoEm: todayStr(),
      autor: opts.autor || "Renato · Gerência de CX",
      titulo: opts.titulo || a.titulo,
      desc: opts.desc || a.desc || "Plano compartilhado pela Unimed.",
      etapa: a.etapa,
      drivers: a.drivers || [],
      ganhoPp: a.sucesso ? a.sucesso.ganhoPp || 0 : opts.ganhoPp || 0,
      duracaoDias: (a.check || []).length * 15 || 60,
      numTarefas: (a.check || []).length,
      responsaveis: [a.resp].filter(Boolean),
      curtidas: 0,
      comentarios: 0,
      reusos: 0,
      tags: ["novo", "campinas"],
      comentariosLista: [],
      telefone: opts.telefone || null,
      email: opts.email || null,
    };
    setCoopPlanos((prev) => [novoPost, ...prev]);
    // Notificação de confirmação da publicação
    setNotifs((prev) => [
      {
        id: Date.now(),
        tipo: "coop_publish",
        titulo: "🤝 Plano publicado no CooperAção Unimed",
        corpo: `"${novoPost.titulo}" agora está visível pra todas as Unimeds filiadas. Outras cooperativas já podem curtir, comentar e reusar. Ganho reportado: +${novoPost.ganhoPp}${a.etapa === "NPS Geral" ? " pts" : "pp"} em ${novoPost.etapa}.`,
        quando: "agora",
        lida: false,
        coopPostId: novoPost.id,
      },
      ...prev,
    ]);
    setTab("coop");
    if (!opts.silent) {
      alert(
        "✅ Plano publicado com sucesso no CooperAção Unimed!\n\nOutras Unimeds já podem ver, comentar e reusar seu plano. Obrigado por apoiar o Sistema Unimed.",
      );
    }
  }
  const toggleCurtida = (id) =>
    setCoopCurtidas((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  const toggleComentarios = (id) =>
    setCoopComentariosAbertos((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  const adicionarComentario = (planoId) => {
    const texto = (coopNovoComentario[planoId] || "").trim();
    if (!texto) return;
    const novoCom = {
      id: `cmt_${planoId}_${Date.now()}`,
      unimed: UNIMED,
      avatar: "🌱",
      autor: "Renato · Gerência CX",
      texto,
      dt: todayStr(),
    };
    setCoopPlanos((prev) =>
      prev.map((p) =>
        p.id === planoId
          ? { ...p, comentariosLista: [...(p.comentariosLista || []), novoCom] }
          : p,
      ),
    );
    setCoopNovoComentario((prev) => ({ ...prev, [planoId]: "" }));
  };
  const toggleCoopDriver = (slug) =>
    setCoopDriverFilter((prev) => {
      const next = new Set(prev);
      if (next.has(slug)) next.delete(slug);
      else next.add(slug);
      return next;
    });
  // Abre o wizard de publicação já no step "publicar" a partir de uma ação com sucesso registrado.
  // Usado quando o usuário quer publicar uma ação já concluída (ou quando clica "Registrar + Publicar" no form).
  function openPublishWizard(acao) {
    if (!acao) return;
    const ganho = acao.sucesso
      ? acao.sucesso.ganhoPp || 0
      : Math.max(
          0,
          (Number(acao.v_atual) || 0) - (Number(acao.v_baseline) || 0),
        );
    setConcluirWizard({
      step: "publicar",
      acao,
      ganhoAuto: ganho,
      ganhoEdit: String(ganho),
      obs: acao.sucesso?.observacao || "",
      autorPub: acao.resp || "Renato · Gerência CX",
      tituloPub: acao.titulo,
      descPub: acao.desc || "",
      telefonePub: "",
      emailPub: "",
    });
  }

  const d = DATA[periodoG];
  const per = PER[periodoG];
  const dj = DATA[pJ];
  const ds = DATA[pS];
  const de = DATA[pE];
  const dd = DATA[pD];
  const db = DATA[pB];

  // Tabs do relatório (sequência principal) e tabs operacionais (fora do relatório)
  const TABS = [
    { id: "executivo", label: "Visão Geral", Icon: BarChart3 },
    { id: "jornada", label: "Jornada", Icon: RouteIcon },
    { id: "drivers", label: "Drivers", Icon: Crosshair },
    { id: "tendencias", label: "Tendências", Icon: TrendingUp },
    { id: "segmentos", label: "Segmentação", Icon: Users },
    { id: "eva", label: "Eva & IA", Icon: Sparkles },
    { id: "benchmark", label: "Benchmarking", Icon: Trophy },
    { id: "quest", label: "Metodologia", Icon: NotebookPen },
  ];
  const TABS_OPS = [
    { id: "acoes", label: "Plano de Ação", Icon: ClipboardList },
    { id: "coop", label: "CooperAção Unimed", Icon: Share2 },
  ];

  const jorGraf = dj.etapas.map((e) => ({
    name: SHORT[e.e] || e.e,
    full: e.e,
    score: e.v,
    meta: (JCFG[e.e] && JCFG[e.e].meta) || 65,
    sys: (JCFG[e.e] && JCFG[e.e].sys) || 55,
    tipo: JCFG[e.e] && JCFG[e.e].tipo,
    color: etSt(e.e, e.v).color,
  }));

  // Single ctx object passed to each sliced view. Over-destructured keys are
  // harmless (resolve to undefined if not present).
  const ctx = {
    periodoG,
    setPeriodoG,
    periodoCustom,
    setPeriodoCustom,
    cidadesAtivas,
    setCidadesAtivas,
    toggleCidade,
    tipoBeneficiario,
    setTipoBeneficiario,
    tipoBenefAberto,
    setTipoBenefAberto,
    todasCidades,
    nenhumaCidade,
    filtroCidadeAberto,
    setFiltroCidadeAberto,
    tab,
    setTab,
    d,
    per,
    DATA,
    metas,
    setMetas,
    editandoMeta,
    setEditandoMeta,
    metasEtapas,
    setMetasEtapas,
    editandoMetaEt,
    setEditandoMetaEt,
    metasCardOpen,
    setMetasCardOpen,
    setMetaEtapa,
    bulkOpen,
    setBulkOpen,
    bulkPct,
    setBulkPct,
    bulkApplyGerais,
    setBulkApplyGerais,
    bulkApplyEtapas,
    setBulkApplyEtapas,
    gamificacaoOn,
    setGamificacaoOn,
    acoes,
    setAcoes,
    acaoSel,
    setAcaoSel,
    concluirWizard,
    setConcluirWizard,
    etapaSel,
    setEtapaSel,
    hiddenLines,
    setHiddenLines,
    toggleLine,
    notifs,
    setNotifs,
    notifOpen,
    setNotifOpen,
    notifsVisiveis,
    notifsOcultas,
    naoLidas,
    marcarComoLida,
    marcarTodasLidas,
    setNotifFeedback,
    ocultarNotif,
    desocultarNotif,
    mostrarOcultos,
    setMostrarOcultos,
    notifModalOpen,
    setNotifModalOpen,
    notifModalFiltro,
    setNotifModalFiltro,
    evaChats,
    setEvaChats,
    evaChatAtivoId,
    setEvaChatAtivoId,
    historicoEvaVisivel,
    setHistoricoEvaVisivel,
    novoChatEva,
    selecionarChatEva,
    excluirChatEva,
    atualizarMensagensChatEva,
    SAUDACAO_EVA,
    // per-tab UI state
    pE,
    setPE,
    pJ,
    setPJ,
    pD,
    setPD,
    pS,
    setPS,
    pB,
    setPB,
    ds,
    de,
    dd,
    db,
    agrupT,
    setAgrupT,
    driverSortMode,
    setDriverSortMode,
    etapaDriversFilter,
    setEtapaDriversFilter,
    etapaHistSel,
    setEtapaHistSel,
    segIndicador,
    setSegIndicador,
    segHistMode,
    toggleSegHist,
    filtroSt,
    setFiltroSt,
    filtroResp,
    setFiltroResp,
    busca,
    setBusca,
    respDropOpen,
    setRespDropOpen,
    stDropOpen,
    setStDropOpen,
    setPrefilledAcao,
    setVerbatinsModal,
    // action-plan handlers
    delAcao,
    compartilharPlano,
    openPublishWizard,
    // coop handlers
    toggleCurtida,
    toggleComentarios,
    toggleCoopDriver,
    adicionarComentario,
    // derived values
    dj,
    jorGraf,
    coopPlanos,
    setCoopPlanos,
    coopBusca,
    setCoopBusca,
    coopEtapaFilter,
    setCoopEtapaFilter,
    coopCurtidas,
    setCoopCurtidas,
    coopDriverFilter,
    setCoopDriverFilter,
    coopComentariosAbertos,
    setCoopComentariosAbertos,
    coopNovoComentario,
    setCoopNovoComentario,
    sucessoModalOpen,
    setSucessoModalOpen,
    sucessoFiltroPeriodo,
    setSucessoFiltroPeriodo,
    modal,
    setModal,
  };

  const btnStyle = (active) => ({
    display: "flex",
    alignItems: "center",
    gap: 5,
    background: active ? C.surface : "transparent",
    color: active ? C.text : C.text3,
    border: "none",
    borderRadius: 8,
    padding: "5px 12px",
    fontSize: 12,
    fontWeight: active ? 600 : 400,
    cursor: "pointer",
    transition: "all .15s",
    boxShadow: active ? "0 1px 3px rgba(0,0,0,.08)" : "none",
    whiteSpace: "nowrap",
  });

  return (
    <div
      id="dashboard-root"
      style={{
        fontFamily: F.text,
        background: C.bg,
        minHeight: "100vh",
        color: C.text,
      }}
    >
      {/* Topbar */}
      <div
        className="no-print"
        style={{
          background: "rgba(255,255,255,.82)",
          backdropFilter: "blur(20px)",
          WebkitBackdropFilter: "blur(20px)",
          borderBottom: `1px solid ${C.border}`,
          padding: "0 24px",
          display: "flex",
          alignItems: "center",
          gap: 14,
          height: 52,
          position: "sticky",
          top: 0,
          zIndex: 100,
        }}
      >
        <img
          src={Logo}
          alt="121"
          style={{
            width: 32,
            height: 32,
            borderRadius: "50%",
            objectFit: "cover",
            flexShrink: 0,
          }}
        />
        <div>
          <div
            style={{
              fontWeight: 700,
              fontSize: 15,
              letterSpacing: "-0.03em",
              lineHeight: 1,
              color: C.text,
            }}
          >
            121 Insights
          </div>
          <div
            style={{
              fontSize: 9,
              color: C.text4,
              fontWeight: 500,
              letterSpacing: "0.02em",
            }}
          >
            by 121Labs
          </div>
        </div>
        <div style={{ width: 1, height: 20, background: C.border }} />
        <div style={{ display: "flex", alignItems: "center", gap: 5 }}>
          <div
            style={{
              width: 7,
              height: 7,
              borderRadius: "50%",
              background: C.green,
            }}
          />
          <span style={{ fontSize: 13, fontWeight: 500, color: C.text2 }}>
            {UNIMED}
          </span>
        </div>
        <div style={{ width: 1, height: 20, background: C.border }} />
        <span style={{ fontSize: 12, color: C.text4 }}>
          Jornada de Experiência do Beneficiário
        </span>
        <div style={{ flex: 1 }} />
        <button
          onClick={() => setModal("pdf")}
          title="Exportar PDF"
          style={{
            display: "flex",
            alignItems: "center",
            gap: 5,
            background: C.surf3,
            border: `1px solid ${C.border}`,
            borderRadius: 10,
            padding: "6px 12px",
            cursor: "pointer",
            fontSize: 12,
            fontWeight: 500,
            color: C.text2,
            transition: "all .15s",
          }}
        >
          <Download size={13} /> Exportar PDF
        </button>

        {/* Badge de engajamento amostral — clica e abre modal */}
        <button
          onClick={() => setEngajamentoModalOpen(true)}
          title="Clique para ver detalhes da amostra"
          style={{
            display: "flex",
            alignItems: "center",
            gap: 6,
            background: C.surf3,
            border: `1px solid ${C.border}`,
            borderRadius: 10,
            padding: "6px 12px",
            cursor: "pointer",
            fontSize: 12,
            fontWeight: 500,
            color: C.text2,
            transition: "all .15s",
            fontFamily: "inherit",
          }}
        >
          <Activity size={13} color={de.eng.resp >= 400 ? C.g700 : C.orange} />
          <span>
            n=<b style={{ color: C.text }}>{de.eng.resp.toLocaleString()}</b>
          </span>
          <span style={{ color: C.text4 }}>·</span>
          <span
            style={{
              color: de.eng.resp >= 400 ? C.g700 : C.orange,
              fontWeight: 600,
            }}
          >
            ±{moe(de.eng.resp)}%
          </span>
        </button>

        {/* Filtro global de cidade */}
        <div style={{ position: "relative" }}>
          <button
            onClick={() => setFiltroCidadeAberto((o) => !o)}
            title="Filtrar por cidade"
            style={{
              display: "flex",
              alignItems: "center",
              gap: 6,
              maxWidth: 280,
              background: filtroCidadeAberto
                ? C.greenBg
                : todasCidades
                  ? C.surf3
                  : C.greenBg,
              border: `1px solid ${filtroCidadeAberto || !todasCidades ? C.g700 : C.border}`,
              borderRadius: 10,
              padding: "6px 12px",
              cursor: "pointer",
              fontSize: 12,
              fontWeight: 500,
              color: filtroCidadeAberto || !todasCidades ? C.g700 : C.text2,
              transition: "all .15s",
              fontFamily: "inherit",
            }}
          >
            <MapPin size={13} style={{ flexShrink: 0 }} />
            <span
              style={{
                whiteSpace: "nowrap",
                overflow: "hidden",
                textOverflow: "ellipsis",
                minWidth: 0,
              }}
            >
              {todasCidades
                ? `Todas as ${CIDADES_COBERTURA.length} cidades`
                : nenhumaCidade
                  ? "Nenhuma cidade"
                  : cidadesAtivas.length <= 2
                    ? cidadesAtivas.join(", ")
                    : `${cidadesAtivas.slice(0, 2).join(", ")} +${cidadesAtivas.length - 2}`}
            </span>
            <ChevronDown
              size={12}
              style={{
                flexShrink: 0,
                transform: filtroCidadeAberto ? "rotate(180deg)" : "none",
                transition: "transform .15s",
              }}
            />
          </button>
          {filtroCidadeAberto && (
            <>
              <div
                onClick={() => setFiltroCidadeAberto(false)}
                style={{ position: "fixed", inset: 0, zIndex: 199 }}
              />
              <div
                style={{
                  position: "absolute",
                  top: "calc(100% + 8px)",
                  right: 0,
                  minWidth: 240,
                  background: C.surface,
                  border: `1px solid ${C.border}`,
                  borderRadius: 12,
                  boxShadow: "0 12px 32px rgba(0,0,0,.16)",
                  zIndex: 200,
                  overflow: "hidden",
                }}
              >
                <div
                  style={{
                    padding: "12px 14px",
                    borderBottom: `1px solid ${C.border}`,
                    background: C.surf3,
                  }}
                >
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                    }}
                  >
                    <div
                      style={{
                        fontSize: 11,
                        fontWeight: 700,
                        color: C.text,
                        textTransform: "uppercase",
                        letterSpacing: "0.05em",
                      }}
                    >
                      Filtrar por cidade
                    </div>
                    <button
                      onClick={() =>
                        setCidadesAtivas(todasCidades ? [] : CIDADES_COBERTURA)
                      }
                      style={{
                        background: "none",
                        border: "none",
                        cursor: "pointer",
                        fontSize: 10,
                        color: C.g700,
                        fontWeight: 600,
                        fontFamily: "inherit",
                        padding: 0,
                      }}
                    >
                      {todasCidades ? "Limpar" : "Selecionar todas"}
                    </button>
                  </div>
                  <div style={{ fontSize: 10, color: C.text4, marginTop: 3 }}>
                    {cidadesAtivas.length} de {CIDADES_COBERTURA.length}{" "}
                    selecionadas
                  </div>
                </div>
                <div
                  style={{
                    maxHeight: 280,
                    overflowY: "auto",
                    padding: "6px 0",
                  }}
                >
                  {CIDADES_COBERTURA.map((c) => {
                    const active = cidadesAtivas.includes(c);
                    return (
                      <div
                        key={c}
                        onClick={() => toggleCidade(c)}
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: 10,
                          padding: "9px 14px",
                          cursor: "pointer",
                          background: active ? `${C.greenBg}` : "transparent",
                          transition: "background .12s",
                        }}
                        onMouseEnter={(e) => {
                          if (!active)
                            e.currentTarget.style.background = C.surf3;
                        }}
                        onMouseLeave={(e) => {
                          if (!active)
                            e.currentTarget.style.background = "transparent";
                        }}
                      >
                        <div
                          style={{
                            width: 16,
                            height: 16,
                            borderRadius: 4,
                            border: `1.5px solid ${active ? C.g700 : C.border}`,
                            background: active ? C.g700 : "#FFFFFF",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            flexShrink: 0,
                          }}
                        >
                          {active && (
                            <CheckCircle2
                              size={12}
                              color="#FFFFFF"
                              strokeWidth={3}
                            />
                          )}
                        </div>
                        <span
                          style={{
                            fontSize: 12,
                            color: active ? C.g700 : C.text2,
                            fontWeight: active ? 600 : 500,
                          }}
                        >
                          {c}
                        </span>
                      </div>
                    );
                  })}
                </div>
                <div
                  style={{
                    padding: "10px 14px",
                    borderTop: `1px solid ${C.border}`,
                    background: C.surf3,
                    fontSize: 9,
                    color: C.text4,
                    lineHeight: 1.5,
                    fontStyle: "italic",
                  }}
                >
                  ⓘ Quando o backend estiver conectado, a seleção filtrará
                  respondentes em todas as visualizações. Hoje os números são
                  consolidados de todas as cidades.
                </div>
              </div>
            </>
          )}
        </div>

        {/* Filtro tipo de beneficiário — Próprio Unimed vs Intercâmbio (de outras Unimeds) */}
        <div style={{ position: "relative" }}>
          <button
            onClick={() => setTipoBenefAberto((o) => !o)}
            title="Filtrar por tipo de beneficiário"
            style={{
              display: "flex",
              alignItems: "center",
              gap: 6,
              background: tipoBeneficiario !== "todos" ? C.greenBg : C.surf3,
              border: `1px solid ${tipoBeneficiario !== "todos" ? C.g700 : C.border}`,
              borderRadius: 10,
              padding: "6px 12px",
              cursor: "pointer",
              fontSize: 12,
              fontWeight: 500,
              color: tipoBeneficiario !== "todos" ? C.g700 : C.text2,
              transition: "all .15s",
              fontFamily: "inherit",
            }}
          >
            <UserCheck size={13} />
            <span style={{ whiteSpace: "nowrap" }}>
              {tipoBeneficiario === "todos"
                ? "Todos os benef."
                : tipoBeneficiario === "proprio"
                  ? "Próprios"
                  : "Intercâmbio"}
            </span>
            <ChevronDown
              size={12}
              style={{
                transform: tipoBenefAberto ? "rotate(180deg)" : "none",
                transition: "transform .15s",
              }}
            />
          </button>
          {tipoBenefAberto && (
            <>
              <div
                onClick={() => setTipoBenefAberto(false)}
                style={{ position: "fixed", inset: 0, zIndex: 199 }}
              />
              <div
                style={{
                  position: "absolute",
                  top: "calc(100% + 8px)",
                  right: 0,
                  minWidth: 280,
                  background: C.surface,
                  border: `1px solid ${C.border}`,
                  borderRadius: 12,
                  boxShadow: "0 12px 32px rgba(0,0,0,.16)",
                  zIndex: 200,
                  overflow: "hidden",
                }}
              >
                <div
                  style={{
                    padding: "12px 14px",
                    borderBottom: `1px solid ${C.border}`,
                    background: C.surf3,
                  }}
                >
                  <div
                    style={{
                      fontSize: 11,
                      fontWeight: 700,
                      color: C.text,
                      textTransform: "uppercase",
                      letterSpacing: "0.05em",
                    }}
                  >
                    Tipo de beneficiário
                  </div>
                  <div style={{ fontSize: 10, color: C.text4, marginTop: 3 }}>
                    Próprios da {UNIMED} ou intercâmbio (de outras Unimeds)
                  </div>
                </div>
                <div style={{ padding: "6px 0" }}>
                  {[
                    {
                      v: "todos",
                      label: "Todos os beneficiários",
                      desc: "Próprios + intercâmbio combinados",
                      ico: Users,
                    },
                    {
                      v: "proprio",
                      label: `Próprios da ${UNIMED}`,
                      desc: "Beneficiários contratados nesta cooperativa",
                      ico: UserCheck,
                    },
                    {
                      v: "intercambio",
                      label: "Intercâmbio",
                      desc: "Beneficiários de outras Unimeds atendidos aqui",
                      ico: Share2,
                    },
                  ].map((opt) => {
                    const active = tipoBeneficiario === opt.v;
                    const Ico = opt.ico;
                    return (
                      <div
                        key={opt.v}
                        onClick={() => {
                          setTipoBeneficiario(opt.v);
                          setTipoBenefAberto(false);
                        }}
                        style={{
                          display: "flex",
                          alignItems: "flex-start",
                          gap: 10,
                          padding: "10px 14px",
                          cursor: "pointer",
                          background: active ? C.greenBg : "transparent",
                          transition: "background .12s",
                        }}
                        onMouseEnter={(e) => {
                          if (!active)
                            e.currentTarget.style.background = C.surf3;
                        }}
                        onMouseLeave={(e) => {
                          if (!active)
                            e.currentTarget.style.background = "transparent";
                        }}
                      >
                        <div
                          style={{
                            width: 18,
                            height: 18,
                            borderRadius: 9,
                            border: `1.5px solid ${active ? C.g700 : C.border}`,
                            background: active ? C.g700 : "#FFFFFF",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            flexShrink: 0,
                            marginTop: 1,
                          }}
                        >
                          {active && (
                            <div
                              style={{
                                width: 8,
                                height: 8,
                                borderRadius: 4,
                                background: "#FFFFFF",
                              }}
                            />
                          )}
                        </div>
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div
                            style={{
                              display: "flex",
                              alignItems: "center",
                              gap: 6,
                            }}
                          >
                            <Ico size={12} color={active ? C.g700 : C.text3} />
                            <span
                              style={{
                                fontSize: 12,
                                color: active ? C.g700 : C.text2,
                                fontWeight: active ? 600 : 500,
                              }}
                            >
                              {opt.label}
                            </span>
                          </div>
                          <div
                            style={{
                              fontSize: 10,
                              color: C.text4,
                              marginTop: 2,
                            }}
                          >
                            {opt.desc}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
                <div
                  style={{
                    padding: "10px 14px",
                    borderTop: `1px solid ${C.border}`,
                    background: C.surf3,
                    fontSize: 9,
                    color: C.text4,
                    lineHeight: 1.5,
                    fontStyle: "italic",
                  }}
                >
                  ⓘ Quando o backend estiver conectado, este filtro segmentará
                  todos os indicadores. Diferenciar próprios de intercâmbio é
                  crítico — beneficiários de intercâmbio têm jornada diferente
                  (acesso à rede, autorização, regras específicas) e impactam
                  métricas de forma distinta.
                </div>
              </div>
            </>
          )}
        </div>

        <PeriodPillBig
          value={periodoG}
          onChange={setPeriodoG}
          custom={periodoCustom}
          onCustomChange={setPeriodoCustom}
        />

        {/* Notificações (mensagens da Hazo.ai pro cliente) */}
        <div style={{ position: "relative" }}>
          <button
            onClick={() => setNotifOpen((o) => !o)}
            title="Notificações"
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              width: 34,
              height: 34,
              background: notifOpen ? C.greenBg : C.surf3,
              border: `1px solid ${notifOpen ? C.g700 : C.border}`,
              borderRadius: 10,
              cursor: "pointer",
              transition: "all .15s",
              position: "relative",
              fontFamily: "inherit",
            }}
          >
            <Bell size={15} color={notifOpen ? C.g700 : C.text2} />
            {naoLidas > 0 && (
              <div
                style={{
                  position: "absolute",
                  top: -3,
                  right: -3,
                  minWidth: 16,
                  height: 16,
                  padding: "0 4px",
                  background: C.red,
                  color: "#FFFFFF",
                  borderRadius: 8,
                  fontSize: 9,
                  fontWeight: 700,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  border: `2px solid ${C.surface}`,
                  boxShadow: "0 1px 3px rgba(220,38,38,.4)",
                }}
              >
                {naoLidas}
              </div>
            )}
          </button>
          {notifOpen && (
            <>
              <div
                onClick={() => setNotifOpen(false)}
                style={{ position: "fixed", inset: 0, zIndex: 199 }}
              />
              <div
                style={{
                  position: "absolute",
                  top: "calc(100% + 8px)",
                  right: 0,
                  width: 380,
                  maxHeight: 520,
                  background: C.surface,
                  border: `1px solid ${C.border}`,
                  borderRadius: 14,
                  boxShadow: "0 16px 48px rgba(0,0,0,.18)",
                  zIndex: 200,
                  overflow: "hidden",
                  display: "flex",
                  flexDirection: "column",
                }}
              >
                {/* Header do popover */}
                <div
                  style={{
                    padding: "14px 16px",
                    borderBottom: `1px solid ${C.border}`,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    background: `linear-gradient(135deg,${C.greenBg} 0%,${C.surface} 100%)`,
                  }}
                >
                  <div
                    style={{ display: "flex", alignItems: "center", gap: 8 }}
                  >
                    <div
                      style={{
                        width: 28,
                        height: 28,
                        background: C.g700,
                        borderRadius: 8,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                      }}
                    >
                      <Bell size={14} color="#FFFFFF" />
                    </div>
                    <div>
                      <div
                        style={{
                          fontSize: 13,
                          fontWeight: 700,
                          color: C.text,
                          letterSpacing: "-0.01em",
                        }}
                      >
                        Notificações
                      </div>
                      <div style={{ fontSize: 10, color: C.text4 }}>
                        {naoLidas > 0
                          ? `${naoLidas} não ${naoLidas === 1 ? "lida" : "lidas"}`
                          : "Tudo em dia"}
                      </div>
                    </div>
                  </div>
                  {naoLidas > 0 && (
                    <button
                      onClick={marcarTodasLidas}
                      style={{
                        background: "transparent",
                        border: "none",
                        fontSize: 10,
                        fontWeight: 600,
                        color: C.g700,
                        cursor: "pointer",
                        fontFamily: "inherit",
                        padding: "4px 8px",
                        borderRadius: 6,
                      }}
                    >
                      Marcar todas
                    </button>
                  )}
                </div>
                {/* Lista de notificações */}
                <div style={{ flex: 1, overflowY: "auto" }}>
                  {notifsVisiveis.length === 0 ? (
                    <div
                      style={{
                        padding: "32px 20px",
                        textAlign: "center",
                        color: C.text4,
                        fontSize: 12,
                      }}
                    >
                      Nenhuma mensagem ainda.
                    </div>
                  ) : (
                    notifsVisiveis.slice(0, 6).map((n) => {
                      const cores = {
                        alerta: { ico: CircleAlert, cor: C.red, bg: "#FEE2E2" },
                        insight: {
                          ico: Lightbulb,
                          cor: C.purple || "#7C68EE",
                          bg: "#F0EDFF",
                        },
                        plano: {
                          ico: CheckCircle2,
                          cor: C.g700,
                          bg: C.greenBg,
                        },
                        comunicado: {
                          ico: MessageSquare,
                          cor: C.blue,
                          bg: C.blueBg,
                        },
                        conquista: {
                          ico: Trophy,
                          cor: "#D97706",
                          bg: "#FEF3C7",
                        },
                        briefing: { ico: Sparkles, cor: C.p500, bg: "#F0EDFF" },
                        eva_plano: {
                          ico: Brain,
                          cor: C.p700 || C.p500,
                          bg: "#EEE9FF",
                        },
                        coop_publish: {
                          ico: Share2,
                          cor: C.purple || "#7C68EE",
                          bg: C.purpleBg || "#F0EDFF",
                        },
                      };
                      const cfg = cores[n.tipo] || cores.comunicado;
                      const Ico = cfg.ico;
                      const isEvaPlano =
                        n.tipo === "eva_plano" && n.planoSugerido;
                      const isBriefing = n.tipo === "briefing";
                      return (
                        <div
                          key={n.id}
                          onClick={() => marcarComoLida(n.id)}
                          style={{
                            padding: "12px 16px",
                            borderBottom: `1px solid ${C.border}`,
                            cursor: "pointer",
                            background: n.lida ? "transparent" : `${cfg.cor}08`,
                            display: "flex",
                            gap: 11,
                            transition: "all .15s",
                            position: "relative",
                          }}
                        >
                          {!n.lida && (
                            <div
                              style={{
                                position: "absolute",
                                left: 6,
                                top: "50%",
                                transform: "translateY(-50%)",
                                width: 6,
                                height: 6,
                                borderRadius: "50%",
                                background: cfg.cor,
                              }}
                            />
                          )}
                          <div
                            style={{
                              width: 30,
                              height: 30,
                              borderRadius: 8,
                              background: cfg.bg,
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              flexShrink: 0,
                              marginLeft: 6,
                            }}
                          >
                            <Ico size={14} color={cfg.cor} />
                          </div>
                          <div style={{ flex: 1, minWidth: 0 }}>
                            <div
                              style={{
                                display: "flex",
                                alignItems: "baseline",
                                gap: 6,
                                marginBottom: 2,
                              }}
                            >
                              <div
                                style={{
                                  fontSize: 12,
                                  fontWeight: n.lida ? 500 : 700,
                                  color: C.text,
                                  letterSpacing: "-0.01em",
                                  flex: 1,
                                  minWidth: 0,
                                  overflow: "hidden",
                                  textOverflow: "ellipsis",
                                }}
                              >
                                {n.titulo}
                              </div>
                              {isBriefing && n.tempoLeitura && (
                                <span
                                  style={{
                                    fontSize: 9,
                                    color: cfg.cor,
                                    fontWeight: 700,
                                    background: cfg.bg,
                                    padding: "1px 6px",
                                    borderRadius: 20,
                                    flexShrink: 0,
                                  }}
                                >
                                  ⏱ {n.tempoLeitura}
                                </span>
                              )}
                              <div
                                style={{
                                  fontSize: 9,
                                  color: C.text4,
                                  flexShrink: 0,
                                }}
                              >
                                {n.quando}
                              </div>
                            </div>
                            <div
                              style={{
                                fontSize: 11,
                                color: C.text3,
                                lineHeight: 1.5,
                              }}
                            >
                              {n.corpo}
                            </div>
                            {/* Botão especial pro eva_plano */}
                            {isEvaPlano && (
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  criarPlanoSugerido(n);
                                }}
                                style={{
                                  marginTop: 8,
                                  display: "inline-flex",
                                  alignItems: "center",
                                  gap: 5,
                                  background: cfg.cor,
                                  color: "#FFFFFF",
                                  border: "none",
                                  borderRadius: 8,
                                  padding: "6px 12px",
                                  fontSize: 10,
                                  fontWeight: 700,
                                  cursor: "pointer",
                                  fontFamily: "inherit",
                                  transition: "all .15s",
                                  boxShadow: `0 2px 8px ${cfg.cor}40`,
                                }}
                              >
                                <Sparkles size={11} /> Criar plano de ação
                                automaticamente
                              </button>
                            )}
                            {/* Barra de feedback inline (útil / não gostei / ocultar) */}
                            <div
                              style={{
                                display: "flex",
                                alignItems: "center",
                                gap: 4,
                                marginTop: 8,
                                paddingTop: 6,
                                borderTop: `1px dashed ${C.border}`,
                              }}
                            >
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setNotifFeedback(n.id, "util");
                                }}
                                title="Útil"
                                style={{
                                  display: "flex",
                                  alignItems: "center",
                                  gap: 3,
                                  background:
                                    n.feedback === "util"
                                      ? C.greenBg
                                      : "transparent",
                                  border: `1px solid ${n.feedback === "util" ? C.g700 : C.border}`,
                                  borderRadius: 6,
                                  padding: "3px 7px",
                                  cursor: "pointer",
                                  fontSize: 10,
                                  fontWeight: 600,
                                  color:
                                    n.feedback === "util" ? C.g700 : C.text4,
                                  fontFamily: "inherit",
                                  transition: "all .15s",
                                }}
                              >
                                <ThumbsUp size={10} /> Útil
                              </button>
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setNotifFeedback(n.id, "naoGostei");
                                }}
                                title="Não gostei"
                                style={{
                                  display: "flex",
                                  alignItems: "center",
                                  gap: 3,
                                  background:
                                    n.feedback === "naoGostei"
                                      ? "#FEE2E2"
                                      : "transparent",
                                  border: `1px solid ${n.feedback === "naoGostei" ? C.red : C.border}`,
                                  borderRadius: 6,
                                  padding: "3px 7px",
                                  cursor: "pointer",
                                  fontSize: 10,
                                  fontWeight: 600,
                                  color:
                                    n.feedback === "naoGostei"
                                      ? C.red
                                      : C.text4,
                                  fontFamily: "inherit",
                                  transition: "all .15s",
                                }}
                              >
                                <ThumbsDown size={10} /> Não gostei
                              </button>
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  ocultarNotif(n.id);
                                }}
                                title="Ocultar esta notificação"
                                style={{
                                  display: "flex",
                                  alignItems: "center",
                                  gap: 3,
                                  background: "transparent",
                                  border: `1px solid ${C.border}`,
                                  borderRadius: 6,
                                  padding: "3px 7px",
                                  cursor: "pointer",
                                  fontSize: 10,
                                  fontWeight: 600,
                                  color: C.text4,
                                  fontFamily: "inherit",
                                  marginLeft: "auto",
                                  transition: "all .15s",
                                }}
                              >
                                <EyeOff size={10} /> Ocultar
                              </button>
                            </div>
                          </div>
                        </div>
                      );
                    })
                  )}
                  {/* Seção de ocultos — colapsável, só aparece se houver itens ocultos */}
                  {notifsOcultas.length > 0 && (
                    <div
                      style={{
                        borderTop: `1px solid ${C.border}`,
                        background: C.surf3,
                      }}
                    >
                      <button
                        onClick={() => setMostrarOcultos((m) => !m)}
                        style={{
                          width: "100%",
                          padding: "10px 16px",
                          background: "transparent",
                          border: "none",
                          display: "flex",
                          alignItems: "center",
                          gap: 8,
                          cursor: "pointer",
                          fontFamily: "inherit",
                          fontSize: 11,
                          fontWeight: 600,
                          color: C.text3,
                          textAlign: "left",
                        }}
                      >
                        <EyeOff size={12} />
                        <span style={{ flex: 1 }}>
                          {notifsOcultas.length}{" "}
                          {notifsOcultas.length === 1
                            ? "ocultada"
                            : "ocultadas"}
                        </span>
                        {mostrarOcultos ? (
                          <ChevronUp size={12} />
                        ) : (
                          <ChevronDown size={12} />
                        )}
                      </button>
                      {mostrarOcultos && (
                        <div
                          style={{
                            borderTop: `1px dashed ${C.border}`,
                            background: C.surface,
                          }}
                        >
                          {notifsOcultas.map((n) => {
                            const coresOc = {
                              alerta: { ico: CircleAlert, cor: C.red },
                              insight: {
                                ico: Lightbulb,
                                cor: C.purple || "#7C68EE",
                              },
                              plano: { ico: CheckCircle2, cor: C.g700 },
                              comunicado: { ico: MessageSquare, cor: C.blue },
                              conquista: { ico: Trophy, cor: "#D97706" },
                              briefing: { ico: Sparkles, cor: C.p500 },
                              eva_plano: { ico: Brain, cor: C.p700 || C.p500 },
                              coop_publish: {
                                ico: Share2,
                                cor: C.purple || "#7C68EE",
                              },
                            };
                            const cfgOc = coresOc[n.tipo] || coresOc.comunicado;
                            const IcoOc = cfgOc.ico;
                            return (
                              <div
                                key={n.id}
                                style={{
                                  padding: "10px 16px",
                                  borderBottom: `1px solid ${C.border}`,
                                  display: "flex",
                                  gap: 10,
                                  opacity: 0.65,
                                }}
                              >
                                <div
                                  style={{
                                    width: 24,
                                    height: 24,
                                    borderRadius: 6,
                                    background: C.surf3,
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    flexShrink: 0,
                                  }}
                                >
                                  <IcoOc size={12} color={cfgOc.cor} />
                                </div>
                                <div style={{ flex: 1, minWidth: 0 }}>
                                  <div
                                    style={{
                                      fontSize: 11,
                                      fontWeight: 600,
                                      color: C.text3,
                                      whiteSpace: "nowrap",
                                      overflow: "hidden",
                                      textOverflow: "ellipsis",
                                    }}
                                  >
                                    {n.titulo}
                                  </div>
                                  <div
                                    style={{
                                      fontSize: 10,
                                      color: C.text4,
                                      lineHeight: 1.4,
                                      display: "-webkit-box",
                                      WebkitLineClamp: 2,
                                      WebkitBoxOrient: "vertical",
                                      overflow: "hidden",
                                    }}
                                  >
                                    {n.corpo}
                                  </div>
                                </div>
                                <button
                                  onClick={() => desocultarNotif(n.id)}
                                  title="Mostrar de novo"
                                  style={{
                                    display: "flex",
                                    alignItems: "center",
                                    gap: 3,
                                    background: "transparent",
                                    border: `1px solid ${C.border}`,
                                    borderRadius: 6,
                                    padding: "3px 7px",
                                    cursor: "pointer",
                                    fontSize: 9,
                                    fontWeight: 600,
                                    color: C.g700,
                                    fontFamily: "inherit",
                                    alignSelf: "center",
                                    flexShrink: 0,
                                  }}
                                >
                                  <Eye size={10} /> Mostrar
                                </button>
                              </div>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  )}
                </div>
                {/* Footer do popover */}
                <div
                  style={{
                    padding: "10px 16px",
                    borderTop: `1px solid ${C.border}`,
                    background: C.surf3,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                  }}
                >
                  <span style={{ fontSize: 10, color: C.text4 }}>
                    {notifsVisiveis.length > 6
                      ? `Mostrando 6 de ${notifsVisiveis.length}`
                      : `${notifsVisiveis.length} notificaç${notifsVisiveis.length === 1 ? "ão" : "ões"}`}
                  </span>
                  <button
                    onClick={() => {
                      setNotifOpen(false);
                      setNotifModalOpen(true);
                    }}
                    style={{
                      background: C.g700,
                      border: "none",
                      borderRadius: 7,
                      padding: "5px 12px",
                      fontSize: 10,
                      fontWeight: 700,
                      color: "#FFFFFF",
                      cursor: "pointer",
                      fontFamily: "inherit",
                      display: "flex",
                      alignItems: "center",
                      gap: 4,
                    }}
                  >
                    Ver todas <ArrowUpRight size={11} />
                  </button>
                </div>
              </div>
            </>
          )}
        </div>

        <div
          style={{
            width: 30,
            height: 30,
            background: C.g700,
            borderRadius: "50%",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            fontSize: 12,
            fontWeight: 700,
            color: "#FFFFFF",
            cursor: "pointer",
          }}
        >
          R
        </div>
      </div>

      {/* -- Tabs -- */}
      <div
        className="no-print"
        style={{
          background: "rgba(255,255,255,.9)",
          backdropFilter: "blur(10px)",
          borderBottom: `1px solid ${C.border}`,
          padding: "0 24px",
          display: "flex",
          alignItems: "center",
          gap: 14,
          overflowX: "auto",
        }}
      >
        {/* Grupo 1 — Tabs do relatório */}
        <div
          style={{
            display: "flex",
            background: C.surf3,
            borderRadius: 10,
            padding: 3,
            gap: 1,
            margin: "7px 0",
          }}
        >
          {TABS.map(({ id, label, Icon }) => (
            <button
              key={id}
              onClick={() => setTab(id)}
              style={btnStyle(tab === id)}
            >
              <Icon size={12} />
              {label}
            </button>
          ))}
        </div>
        {/* Grupo 2 — Tabs operacionais (Plano de Ação + CooperAção), mesmo layout, visualmente sombreadas */}
        <div
          style={{
            display: "flex",
            background: "linear-gradient(135deg, #EEF2F7 0%, #E2E8F0 100%)",
            border: `1px dashed ${C.border2 || "#CBD5E1"}`,
            borderRadius: 10,
            padding: 3,
            gap: 1,
            margin: "7px 0",
            boxShadow: "inset 0 1px 2px rgba(15,23,42,.06)",
          }}
        >
          {TABS_OPS.map(({ id, label, Icon }) => (
            <button
              key={id}
              onClick={() => setTab(id)}
              style={{
                ...btnStyle(tab === id),
                background: tab === id ? C.surface : "transparent",
                color: tab === id ? C.text : C.text3,
                opacity: tab === id ? 1 : 0.85,
              }}
            >
              <Icon size={12} />
              {label}
            </button>
          ))}
        </div>
        <div style={{ flex: 1 }} />
        <span style={{ fontSize: 11, color: C.text4, whiteSpace: "nowrap" }}>
          📅 {per.label} · {per.resp.toLocaleString()} resp
        </span>
      </div>

      <div style={{ padding: "20px 24px", maxWidth: 1360, margin: "0 auto" }}>
        <NPSAnchor d={d} per={per} metas={metas} />

        {/* == VISÃO GERAL (Nível 1) == */}
        {tab === "executivo" && <ExecutiveView ctx={ctx} />}
        {/* == JORNADA == */}
        {tab === "jornada" && <JourneyView ctx={ctx} />}

        {tab === "tendencias" && <TrendsView ctx={ctx} />}
        {/* == SEGMENTAÇÃO == */}
        {tab === "segmentos" && <SegmentationView ctx={ctx} />}
        {/* == DRIVERS == */}
        {tab === "drivers" && <DriversView ctx={ctx} />}
        {tab === "eva" && <EvaView ctx={ctx} />}
        {/* == BENCHMARK == */}
        {tab === "benchmark" && <BenchmarkingView ctx={ctx} />}
        {/* == PLANO DE AÇÃO == */}
        {tab === "acoes" && <ActionPlanView ctx={ctx} />}
        {/* == PLANO DE AÇÃO COOPERATIVO SISTEMA UNIMED (feed estilo rede social) == */}
        {tab === "coop" && <CooperacaoView ctx={ctx} />}
        {/* == QUESTIONÁRIO == */}
        {tab === "quest" && <VerbatinsView ctx={ctx} />}
      </div>
      {/* Wizard de Conclusão — disparado quando um plano transita para "concluido" sem sucesso registrado */}
      {concluirWizard &&
        (() => {
          const w = concluirWizard;
          const ganhoNum = Number(w.ganhoEdit) || 0;
          const isPositivo = w.ganhoAuto > 0;
          const closeWizard = () => setConcluirWizard(null);
          const registrarSucessoStep = () => {
            // Atualiza a ação com sucesso registrado
            const acaoAtualizada = {
              ...w.acao,
              sucesso: {
                ganhoPp: ganhoNum,
                observacao: w.obs,
                registradoEm: todayStr(),
              },
            };
            setAcoes((prev) =>
              prev.map((x) =>
                x.id === acaoAtualizada.id ? acaoAtualizada : x,
              ),
            );
            // Avança pra step de publicação (se ganho positivo) ou fecha
            if (ganhoNum > 0) {
              setConcluirWizard({
                ...w,
                step: "publicar",
                acao: acaoAtualizada,
              });
            } else {
              setConcluirWizard(null);
            }
          };
          const publicarStep = () => {
            // Publica no CooperAção com os campos editados
            compartilharPlano(w.acao, {
              autor: w.autorPub,
              titulo: w.tituloPub,
              desc: w.descPub,
              telefone: w.telefonePub ? w.telefonePub.trim() : null,
              email: w.emailPub ? w.emailPub.trim() : null,
              silent: true,
            });
            setConcluirWizard(null);
            setTimeout(() => {
              alert(
                `✅ Plano publicado no CooperAção Unimed!\n\n"${w.tituloPub}" agora está visível pra todas as Unimeds filiadas. Outras cooperativas vão poder curtir, comentar e reusar seu aprendizado.\n\nObrigado por apoiar o Sistema Unimed. 🤝`,
              );
            }, 100);
          };

          return (
            <div
              style={{
                position: "fixed",
                inset: 0,
                background: "rgba(0,0,0,.5)",
                backdropFilter: "blur(8px)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                zIndex: 400,
                padding: 20,
              }}
              onClick={(e) => e.target === e.currentTarget && closeWizard()}
            >
              <div
                style={{
                  background: C.surface,
                  borderRadius: 20,
                  width: "100%",
                  maxWidth: 580,
                  maxHeight: "92vh",
                  overflowY: "auto",
                  boxShadow: "0 32px 80px rgba(0,0,0,.3)",
                  position: "relative",
                }}
              >
                {/* Header do wizard — muda por step */}
                {w.step === "registrar_sucesso" && (
                  <>
                    <div
                      style={{
                        padding: "22px 26px",
                        background: `linear-gradient(135deg,${C.greenBg} 0%,#ECFDF5 100%)`,
                        borderBottom: `1px solid ${C.g300}`,
                        borderRadius: "20px 20px 0 0",
                        display: "flex",
                        alignItems: "center",
                        gap: 14,
                      }}
                    >
                      <div
                        style={{
                          width: 52,
                          height: 52,
                          borderRadius: 14,
                          background: C.green,
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          flexShrink: 0,
                          boxShadow: "0 4px 12px rgba(22,163,74,.3)",
                        }}
                      >
                        <Trophy size={24} color="#FFFFFF" />
                      </div>
                      <div style={{ flex: 1 }}>
                        <div
                          style={{
                            fontSize: 10,
                            fontWeight: 700,
                            color: C.green,
                            textTransform: "uppercase",
                            letterSpacing: "0.08em",
                          }}
                        >
                          Plano concluído · etapa 1 de 2
                        </div>
                        <div
                          style={{
                            fontSize: 17,
                            fontWeight: 700,
                            color: C.text,
                            letterSpacing: "-0.02em",
                            marginTop: 2,
                          }}
                        >
                          Parabéns! Vamos registrar o sucesso?
                        </div>
                      </div>
                      <button
                        onClick={closeWizard}
                        style={{
                          background: "rgba(255,255,255,.7)",
                          border: `1px solid ${C.border}`,
                          borderRadius: 10,
                          width: 32,
                          height: 32,
                          cursor: "pointer",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          color: C.text3,
                          fontFamily: "inherit",
                        }}
                      >
                        <X size={16} />
                      </button>
                    </div>
                    <div
                      style={{
                        padding: "22px 26px",
                        display: "flex",
                        flexDirection: "column",
                        gap: 16,
                      }}
                    >
                      {/* Card com o delta calculado */}
                      <div
                        style={{
                          background: C.surf3,
                          borderRadius: 14,
                          padding: "16px 18px",
                          border: `1.5px solid ${isPositivo ? C.green : C.border}`,
                        }}
                      >
                        <div
                          style={{
                            fontSize: 10,
                            fontWeight: 700,
                            color: C.text4,
                            textTransform: "uppercase",
                            letterSpacing: "0.06em",
                            marginBottom: 8,
                          }}
                        >
                          {isPositivo
                            ? "🎯 Impacto detectado automaticamente"
                            : "⚠️ Nenhuma melhoria detectada"}
                        </div>
                        <div
                          style={{
                            display: "grid",
                            gridTemplateColumns: "1fr auto 1fr",
                            gap: 14,
                            alignItems: "center",
                            marginBottom: 10,
                          }}
                        >
                          <div
                            style={{
                              textAlign: "center",
                              padding: "10px 12px",
                              background: C.surface,
                              borderRadius: 10,
                              border: `1px solid ${C.border}`,
                            }}
                          >
                            <div
                              style={{
                                fontSize: 9,
                                color: C.text4,
                                fontWeight: 600,
                                textTransform: "uppercase",
                                letterSpacing: "0.04em",
                              }}
                            >
                              Ao criar o plano
                            </div>
                            <div
                              style={{
                                fontSize: 22,
                                fontWeight: 700,
                                color: C.text3,
                                letterSpacing: "-0.03em",
                                marginTop: 2,
                              }}
                            >
                              {w.acao.v_baseline}
                              {w.acao.etapa === "NPS Geral" ? "" : "%"}
                            </div>
                          </div>
                          <div style={{ fontSize: 20, color: C.text4 }}>→</div>
                          <div
                            style={{
                              textAlign: "center",
                              padding: "10px 12px",
                              background: isPositivo ? C.greenBg : C.surface,
                              borderRadius: 10,
                              border: `1px solid ${isPositivo ? C.green : C.border}`,
                            }}
                          >
                            <div
                              style={{
                                fontSize: 9,
                                color: C.text4,
                                fontWeight: 600,
                                textTransform: "uppercase",
                                letterSpacing: "0.04em",
                              }}
                            >
                              Agora (concluído)
                            </div>
                            <div
                              style={{
                                fontSize: 22,
                                fontWeight: 700,
                                color: isPositivo ? C.green : C.text,
                                letterSpacing: "-0.03em",
                                marginTop: 2,
                              }}
                            >
                              {w.acao.v_atual}
                              {w.acao.etapa === "NPS Geral" ? "" : "%"}
                            </div>
                          </div>
                        </div>
                        <div
                          style={{
                            textAlign: "center",
                            padding: "8px 12px",
                            background: isPositivo ? C.green : C.surf3,
                            color: isPositivo ? "#FFFFFF" : C.text3,
                            borderRadius: 10,
                            fontSize: 14,
                            fontWeight: 700,
                            letterSpacing: "-0.02em",
                          }}
                        >
                          {isPositivo
                            ? `✨ Evolução: +${w.ganhoAuto}${w.acao.etapa === "NPS Geral" ? " pts" : "pp"}`
                            : "Não houve melhoria mensurável neste período"}
                        </div>
                      </div>
                      {/* Input editável do ganho + obs */}
                      <div
                        style={{
                          display: "grid",
                          gridTemplateColumns: "1fr 3fr",
                          gap: 10,
                        }}
                      >
                        <div>
                          <div
                            style={{
                              fontSize: 10,
                              fontWeight: 700,
                              color: C.text3,
                              marginBottom: 4,
                              textTransform: "uppercase",
                              letterSpacing: "0.04em",
                            }}
                          >
                            Ganho (pp)
                          </div>
                          <input
                            type="number"
                            value={w.ganhoEdit}
                            step="0.5"
                            onChange={(e) =>
                              setConcluirWizard({
                                ...w,
                                ganhoEdit: e.target.value,
                              })
                            }
                            style={{
                              width: "100%",
                              border: `1.5px solid ${C.g300}`,
                              borderRadius: 10,
                              padding: "10px 12px",
                              fontSize: 18,
                              fontWeight: 700,
                              color: C.green,
                              outline: "none",
                              background: C.surface,
                              fontFamily: "inherit",
                              boxSizing: "border-box",
                            }}
                          />
                        </div>
                        <div>
                          <div
                            style={{
                              fontSize: 10,
                              fontWeight: 700,
                              color: C.text3,
                              marginBottom: 4,
                              textTransform: "uppercase",
                              letterSpacing: "0.04em",
                            }}
                          >
                            Observação sobre o resultado (opcional)
                          </div>
                          <input
                            value={w.obs}
                            onChange={(e) =>
                              setConcluirWizard({ ...w, obs: e.target.value })
                            }
                            placeholder="Como vocês sabem que funcionou? O que foi mais impactante?"
                            style={{
                              width: "100%",
                              border: `1.5px solid ${C.border}`,
                              borderRadius: 10,
                              padding: "10px 12px",
                              fontSize: 12,
                              color: C.text,
                              outline: "none",
                              background: C.surface,
                              fontFamily: "inherit",
                              boxSizing: "border-box",
                            }}
                          />
                        </div>
                      </div>
                      {/* Botões de ação */}
                      <div style={{ display: "flex", gap: 10, marginTop: 4 }}>
                        <button
                          onClick={closeWizard}
                          style={{
                            flex: 1,
                            background: C.surf3,
                            color: C.text3,
                            border: `1px solid ${C.border}`,
                            borderRadius: 12,
                            padding: "11px 0",
                            cursor: "pointer",
                            fontSize: 13,
                            fontWeight: 600,
                            fontFamily: "inherit",
                          }}
                        >
                          Agora não
                        </button>
                        <button
                          onClick={registrarSucessoStep}
                          style={{
                            flex: 2,
                            background: C.green,
                            color: "#FFFFFF",
                            border: "none",
                            borderRadius: 12,
                            padding: "11px 0",
                            cursor: "pointer",
                            fontSize: 13,
                            fontWeight: 700,
                            fontFamily: "inherit",
                            boxShadow: "0 4px 12px rgba(22,163,74,.3)",
                          }}
                        >
                          🏆 Registrar sucesso{isPositivo ? " e continuar" : ""}
                        </button>
                      </div>
                    </div>
                  </>
                )}

                {w.step === "publicar" && (
                  <>
                    <div
                      style={{
                        padding: "22px 26px",
                        background: `linear-gradient(135deg,${C.g800} 0%,${C.p700 || C.p500} 100%)`,
                        borderBottom: "none",
                        borderRadius: "20px 20px 0 0",
                        display: "flex",
                        alignItems: "center",
                        gap: 14,
                      }}
                    >
                      <div
                        style={{
                          width: 52,
                          height: 52,
                          borderRadius: 14,
                          background: "rgba(255,255,255,.15)",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          flexShrink: 0,
                        }}
                      >
                        <Share2 size={24} color="#FFFFFF" />
                      </div>
                      <div style={{ flex: 1 }}>
                        <div
                          style={{
                            fontSize: 10,
                            fontWeight: 700,
                            color: "rgba(255,255,255,.7)",
                            textTransform: "uppercase",
                            letterSpacing: "0.08em",
                          }}
                        >
                          Sucesso registrado · etapa 2 de 2
                        </div>
                        <div
                          style={{
                            fontSize: 17,
                            fontWeight: 700,
                            color: "#FFFFFF",
                            letterSpacing: "-0.02em",
                            marginTop: 2,
                          }}
                        >
                          Compartilhe esse aprendizado!
                        </div>
                      </div>
                      <button
                        onClick={closeWizard}
                        style={{
                          background: "rgba(255,255,255,.15)",
                          border: "none",
                          borderRadius: 10,
                          width: 32,
                          height: 32,
                          cursor: "pointer",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          color: "#FFFFFF",
                          fontFamily: "inherit",
                        }}
                      >
                        <X size={16} />
                      </button>
                    </div>
                    <div
                      style={{
                        padding: "22px 26px",
                        display: "flex",
                        flexDirection: "column",
                        gap: 14,
                      }}
                    >
                      {/* Mensagem de incentivo */}
                      <div
                        style={{
                          background: `linear-gradient(135deg,${C.purpleBg || "#F0EDFF"} 0%,${C.surface} 100%)`,
                          borderRadius: 14,
                          padding: "16px 18px",
                          border: `1.5px solid ${C.purple || "#7C68EE"}40`,
                        }}
                      >
                        <div
                          style={{
                            display: "flex",
                            gap: 10,
                            alignItems: "flex-start",
                          }}
                        >
                          <div
                            style={{
                              fontSize: 26,
                              flexShrink: 0,
                              lineHeight: 1,
                            }}
                          >
                            🤝
                          </div>
                          <div>
                            <div
                              style={{
                                fontSize: 13,
                                fontWeight: 700,
                                color: C.text,
                                marginBottom: 4,
                                letterSpacing: "-0.01em",
                              }}
                            >
                              Outras Unimeds podem aprender com você
                            </div>
                            <div
                              style={{
                                fontSize: 11,
                                color: C.text2,
                                lineHeight: 1.55,
                              }}
                            >
                              Seu plano gerou{" "}
                              <b style={{ color: C.green }}>
                                +{ganhoNum}
                                {w.acao.etapa === "NPS Geral" ? " pts" : "pp"}
                              </b>{" "}
                              de impacto em <b>{w.acao.etapa}</b>. Ao publicar
                              no CooperAção Unimed, você ajuda cooperativas que
                              enfrentam o mesmo desafio e fortalece todo o
                              Sistema Unimed.{" "}
                              <b>
                                O cooperativismo é a força de quem compartilha.
                              </b>
                            </div>
                          </div>
                        </div>
                      </div>

                      {/* Campos editáveis da publicação */}
                      <div>
                        <div
                          style={{
                            fontSize: 10,
                            fontWeight: 700,
                            color: C.text3,
                            marginBottom: 4,
                            textTransform: "uppercase",
                            letterSpacing: "0.04em",
                          }}
                        >
                          Responsável pela publicação
                        </div>
                        <input
                          value={w.autorPub}
                          onChange={(e) =>
                            setConcluirWizard({
                              ...w,
                              autorPub: e.target.value,
                            })
                          }
                          placeholder="Ex: Maria Silva · Gerência CX"
                          style={{
                            width: "100%",
                            border: `1.5px solid ${C.border}`,
                            borderRadius: 10,
                            padding: "10px 12px",
                            fontSize: 13,
                            color: C.text,
                            outline: "none",
                            background: C.surface,
                            fontFamily: "inherit",
                            boxSizing: "border-box",
                          }}
                        />
                        <div
                          style={{
                            fontSize: 9,
                            color: C.text4,
                            marginTop: 3,
                            fontStyle: "italic",
                          }}
                        >
                          Esse nome aparecerá como autor do plano no feed
                          CooperAção. Você pode atribuir a quem liderou a
                          execução.
                        </div>
                      </div>
                      <div>
                        <div
                          style={{
                            fontSize: 10,
                            fontWeight: 700,
                            color: C.text3,
                            marginBottom: 4,
                            textTransform: "uppercase",
                            letterSpacing: "0.04em",
                          }}
                        >
                          Título da publicação
                        </div>
                        <input
                          value={w.tituloPub}
                          onChange={(e) =>
                            setConcluirWizard({
                              ...w,
                              tituloPub: e.target.value,
                            })
                          }
                          style={{
                            width: "100%",
                            border: `1.5px solid ${C.border}`,
                            borderRadius: 10,
                            padding: "10px 12px",
                            fontSize: 13,
                            fontWeight: 600,
                            color: C.text,
                            outline: "none",
                            background: C.surface,
                            fontFamily: "inherit",
                            boxSizing: "border-box",
                          }}
                        />
                      </div>
                      <div>
                        <div
                          style={{
                            fontSize: 10,
                            fontWeight: 700,
                            color: C.text3,
                            marginBottom: 4,
                            textTransform: "uppercase",
                            letterSpacing: "0.04em",
                          }}
                        >
                          Descrição (o que outras Unimeds precisam saber)
                        </div>
                        <textarea
                          value={w.descPub}
                          onChange={(e) =>
                            setConcluirWizard({ ...w, descPub: e.target.value })
                          }
                          rows={3}
                          placeholder="Conte como foi a execução, o que funcionou, quais desafios apareceram..."
                          style={{
                            width: "100%",
                            border: `1.5px solid ${C.border}`,
                            borderRadius: 10,
                            padding: "10px 12px",
                            fontSize: 12,
                            color: C.text,
                            outline: "none",
                            background: C.surface,
                            fontFamily: "inherit",
                            boxSizing: "border-box",
                            resize: "vertical",
                          }}
                        />
                      </div>

                      {/* Contato opcional — telefone/email pra outras Unimeds entrarem em contato */}
                      <div>
                        <div
                          style={{
                            fontSize: 10,
                            fontWeight: 700,
                            color: C.text3,
                            marginBottom: 4,
                            textTransform: "uppercase",
                            letterSpacing: "0.04em",
                            display: "flex",
                            alignItems: "center",
                            gap: 6,
                          }}
                        >
                          Contato para outras Unimeds{" "}
                          <span
                            style={{
                              fontSize: 9,
                              fontWeight: 500,
                              color: C.text4,
                              textTransform: "none",
                              letterSpacing: "normal",
                              fontStyle: "italic",
                            }}
                          >
                            (opcional — só preencha se quiser ser contatado)
                          </span>
                        </div>
                        <div
                          style={{
                            display: "grid",
                            gridTemplateColumns: "1fr 1fr",
                            gap: 8,
                          }}
                        >
                          <div style={{ position: "relative" }}>
                            <Phone
                              size={12}
                              color={C.text4}
                              style={{
                                position: "absolute",
                                left: 11,
                                top: "50%",
                                transform: "translateY(-50%)",
                                pointerEvents: "none",
                              }}
                            />
                            <input
                              type="tel"
                              value={w.telefonePub}
                              onChange={(e) =>
                                setConcluirWizard({
                                  ...w,
                                  telefonePub: e.target.value,
                                })
                              }
                              placeholder="(19) 99999-0000"
                              style={{
                                width: "100%",
                                border: `1.5px solid ${C.border}`,
                                borderRadius: 10,
                                padding: "10px 12px 10px 32px",
                                fontSize: 12,
                                color: C.text,
                                outline: "none",
                                background: C.surface,
                                fontFamily: "inherit",
                                boxSizing: "border-box",
                              }}
                            />
                          </div>
                          <div style={{ position: "relative" }}>
                            <Mail
                              size={12}
                              color={C.text4}
                              style={{
                                position: "absolute",
                                left: 11,
                                top: "50%",
                                transform: "translateY(-50%)",
                                pointerEvents: "none",
                              }}
                            />
                            <input
                              type="email"
                              value={w.emailPub}
                              onChange={(e) =>
                                setConcluirWizard({
                                  ...w,
                                  emailPub: e.target.value,
                                })
                              }
                              placeholder="cx@unimed.com.br"
                              style={{
                                width: "100%",
                                border: `1.5px solid ${C.border}`,
                                borderRadius: 10,
                                padding: "10px 12px 10px 32px",
                                fontSize: 12,
                                color: C.text,
                                outline: "none",
                                background: C.surface,
                                fontFamily: "inherit",
                                boxSizing: "border-box",
                              }}
                            />
                          </div>
                        </div>
                        <div
                          style={{
                            fontSize: 9,
                            color: C.text4,
                            marginTop: 4,
                            fontStyle: "italic",
                            display: "flex",
                            alignItems: "flex-start",
                            gap: 4,
                          }}
                        >
                          <span>ⓘ</span>
                          <span>
                            Cooperativas que quiserem replicar o plano poderão
                            entrar em contato direto. Os dados ficam visíveis no
                            card do feed CooperAção. Deixe em branco para
                            publicar anonimamente.
                          </span>
                        </div>
                      </div>

                      {/* Preview dos drivers que serão publicados */}
                      {(w.acao.drivers || []).length > 0 && (
                        <div>
                          <div
                            style={{
                              fontSize: 10,
                              fontWeight: 700,
                              color: C.text3,
                              marginBottom: 5,
                              textTransform: "uppercase",
                              letterSpacing: "0.04em",
                            }}
                          >
                            Drivers afetados (serão incluídos na publicação)
                          </div>
                          <div
                            style={{
                              display: "flex",
                              flexWrap: "wrap",
                              gap: 5,
                            }}
                          >
                            {(w.acao.drivers || []).map((slug) => {
                              const drv = getDriverInfo(slug);
                              if (!drv) return null;
                              return (
                                <div
                                  key={slug}
                                  style={{
                                    padding: "4px 10px",
                                    borderRadius: 20,
                                    background: C.purpleBg || "#F0EDFF",
                                    border: `1px solid ${C.purple || "#7C68EE"}40`,
                                    fontSize: 10,
                                    fontWeight: 600,
                                    color: C.purple || "#7C68EE",
                                    display: "flex",
                                    alignItems: "center",
                                    gap: 4,
                                  }}
                                >
                                  <span style={{ fontSize: 12 }}>
                                    {drv.icon}
                                  </span>
                                  {drv.label}
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      )}

                      {/* Botões de ação */}
                      <div style={{ display: "flex", gap: 10, marginTop: 4 }}>
                        <button
                          onClick={closeWizard}
                          style={{
                            flex: 1,
                            background: C.surf3,
                            color: C.text3,
                            border: `1px solid ${C.border}`,
                            borderRadius: 12,
                            padding: "11px 0",
                            cursor: "pointer",
                            fontSize: 13,
                            fontWeight: 600,
                            fontFamily: "inherit",
                          }}
                        >
                          Agora não
                        </button>
                        <button
                          onClick={publicarStep}
                          disabled={!w.autorPub.trim() || !w.tituloPub.trim()}
                          style={{
                            flex: 2,
                            background:
                              w.autorPub.trim() && w.tituloPub.trim()
                                ? C.purple || "#7C68EE"
                                : C.border,
                            color: "#FFFFFF",
                            border: "none",
                            borderRadius: 12,
                            padding: "11px 0",
                            cursor:
                              w.autorPub.trim() && w.tituloPub.trim()
                                ? "pointer"
                                : "default",
                            fontSize: 13,
                            fontWeight: 700,
                            fontFamily: "inherit",
                            boxShadow:
                              w.autorPub.trim() && w.tituloPub.trim()
                                ? `0 4px 12px ${C.purple || "#7C68EE"}40`
                                : "none",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            gap: 6,
                          }}
                        >
                          <Share2 size={14} /> Publicar no CooperAção
                        </button>
                      </div>
                    </div>
                  </>
                )}
              </div>
            </div>
          );
        })()}
      {/* Modal fullscreen "Ver todas" notificações */}
      {notifModalOpen &&
        (() => {
          const filtradas = notifsVisiveis.filter((n) => {
            if (notifModalFiltro === "nao_lidas") return !n.lida;
            if (notifModalFiltro === "uteis") return n.feedback === "util";
            return true;
          });
          const cores = {
            alerta: { ico: CircleAlert, cor: C.red, bg: "#FEE2E2" },
            insight: {
              ico: Lightbulb,
              cor: C.purple || "#7C68EE",
              bg: "#F0EDFF",
            },
            plano: { ico: CheckCircle2, cor: C.g700, bg: C.greenBg },
            comunicado: { ico: MessageSquare, cor: C.blue, bg: C.blueBg },
            conquista: { ico: Trophy, cor: "#D97706", bg: "#FEF3C7" },
            briefing: { ico: Sparkles, cor: C.p500, bg: "#F0EDFF" },
            eva_plano: { ico: Brain, cor: C.p700 || C.p500, bg: "#EEE9FF" },
            coop_publish: {
              ico: Share2,
              cor: C.purple || "#7C68EE",
              bg: C.purpleBg || "#F0EDFF",
            },
          };
          return (
            <div
              style={{
                position: "fixed",
                inset: 0,
                background: "rgba(15,23,42,.65)",
                backdropFilter: "blur(10px)",
                zIndex: 500,
                display: "flex",
                flexDirection: "column",
              }}
              onClick={(e) =>
                e.target === e.currentTarget && setNotifModalOpen(false)
              }
            >
              {/* Header */}
              <div
                style={{
                  padding: "22px 32px",
                  background: `linear-gradient(135deg,${C.greenBg} 0%,${C.surface} 100%)`,
                  borderBottom: `1px solid ${C.border}`,
                  display: "flex",
                  alignItems: "center",
                  gap: 16,
                }}
              >
                <div
                  style={{
                    width: 48,
                    height: 48,
                    borderRadius: 14,
                    background: C.g700,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    boxShadow: "0 4px 12px rgba(22,101,52,.25)",
                  }}
                >
                  <Bell size={22} color="#FFFFFF" />
                </div>
                <div style={{ flex: 1 }}>
                  <div
                    style={{
                      fontSize: 11,
                      fontWeight: 700,
                      color: C.g700,
                      textTransform: "uppercase",
                      letterSpacing: "0.08em",
                    }}
                  >
                    Central de notificações
                  </div>
                  <div
                    style={{
                      fontSize: 22,
                      fontWeight: 700,
                      color: C.text,
                      letterSpacing: "-0.02em",
                      marginTop: 2,
                    }}
                  >
                    Todas as notificações
                  </div>
                  <div style={{ fontSize: 12, color: C.text3, marginTop: 2 }}>
                    {notifsVisiveis.length} no total · {naoLidas} não lida
                    {naoLidas === 1 ? "" : "s"}
                  </div>
                </div>
                {naoLidas > 0 && (
                  <button
                    onClick={marcarTodasLidas}
                    style={{
                      background: C.surface,
                      border: `1px solid ${C.border}`,
                      borderRadius: 10,
                      padding: "9px 14px",
                      fontSize: 11,
                      fontWeight: 600,
                      color: C.text2,
                      cursor: "pointer",
                      fontFamily: "inherit",
                    }}
                  >
                    Marcar todas como lidas
                  </button>
                )}
                <button
                  onClick={() => setNotifModalOpen(false)}
                  style={{
                    background: C.surface,
                    border: `1px solid ${C.border}`,
                    borderRadius: 12,
                    width: 40,
                    height: 40,
                    cursor: "pointer",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    color: C.text3,
                    fontFamily: "inherit",
                  }}
                >
                  <X size={18} />
                </button>
              </div>
              {/* Filtros */}
              <div
                style={{
                  padding: "14px 32px",
                  background: C.surface,
                  borderBottom: `1px solid ${C.border}`,
                  display: "flex",
                  alignItems: "center",
                  gap: 8,
                }}
              >
                {[
                  { k: "todas", lbl: "Todas", count: notifsVisiveis.length },
                  { k: "nao_lidas", lbl: "Não lidas", count: naoLidas },
                  {
                    k: "uteis",
                    lbl: "👍 Marcadas como úteis",
                    count: notifsVisiveis.filter((n) => n.feedback === "util")
                      .length,
                  },
                ].map((f) => {
                  const active = notifModalFiltro === f.k;
                  return (
                    <button
                      key={f.k}
                      onClick={() => setNotifModalFiltro(f.k)}
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: 6,
                        padding: "7px 14px",
                        borderRadius: 20,
                        border: `1px solid ${active ? C.g700 : C.border}`,
                        background: active ? C.g700 : C.surface,
                        color: active ? "#FFFFFF" : C.text2,
                        fontSize: 11,
                        fontWeight: 600,
                        cursor: "pointer",
                        fontFamily: "inherit",
                        transition: "all .15s",
                      }}
                    >
                      {f.lbl}
                      <span
                        style={{
                          background: active
                            ? "rgba(255,255,255,.25)"
                            : C.surf3,
                          borderRadius: 20,
                          padding: "1px 7px",
                          fontSize: 9,
                          fontWeight: 700,
                        }}
                      >
                        {f.count}
                      </span>
                    </button>
                  );
                })}
              </div>
              {/* Lista de notificações em layout largo */}
              <div
                style={{
                  flex: 1,
                  overflowY: "auto",
                  padding: "24px 32px",
                  background: C.bg || "#F8FAFC",
                }}
                onClick={(e) =>
                  e.target === e.currentTarget && setNotifModalOpen(false)
                }
              >
                <div
                  style={{
                    maxWidth: 920,
                    margin: "0 auto",
                    display: "flex",
                    flexDirection: "column",
                    gap: 10,
                  }}
                >
                  {filtradas.length === 0 && (
                    <div
                      style={{
                        padding: "60px 20px",
                        textAlign: "center",
                        color: C.text4,
                        fontSize: 13,
                        background: C.surface,
                        borderRadius: 14,
                        border: `1px dashed ${C.border}`,
                      }}
                    >
                      Nenhuma notificação com este filtro.
                    </div>
                  )}
                  {filtradas.map((n) => {
                    const cfg = cores[n.tipo] || cores.comunicado;
                    const Ico = cfg.ico;
                    const isEvaPlano =
                      n.tipo === "eva_plano" && n.planoSugerido;
                    return (
                      <div
                        key={n.id}
                        onClick={() => marcarComoLida(n.id)}
                        style={{
                          background: C.surface,
                          border: `1px solid ${n.lida ? C.border : cfg.cor + "40"}`,
                          borderLeft: `4px solid ${cfg.cor}`,
                          borderRadius: 14,
                          padding: "18px 22px",
                          display: "flex",
                          gap: 16,
                          cursor: "pointer",
                          transition: "all .15s",
                          boxShadow: n.lida
                            ? "none"
                            : `0 2px 12px ${cfg.cor}15`,
                        }}
                      >
                        <div
                          style={{
                            width: 42,
                            height: 42,
                            borderRadius: 11,
                            background: cfg.bg,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            flexShrink: 0,
                          }}
                        >
                          <Ico size={20} color={cfg.cor} />
                        </div>
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div
                            style={{
                              display: "flex",
                              alignItems: "baseline",
                              gap: 8,
                              marginBottom: 4,
                            }}
                          >
                            <div
                              style={{
                                fontSize: 14,
                                fontWeight: n.lida ? 500 : 700,
                                color: C.text,
                                letterSpacing: "-0.01em",
                                flex: 1,
                              }}
                            >
                              {n.titulo}
                            </div>
                            {!n.lida && (
                              <span
                                style={{
                                  fontSize: 9,
                                  fontWeight: 700,
                                  background: cfg.cor,
                                  color: "#FFFFFF",
                                  padding: "2px 8px",
                                  borderRadius: 20,
                                  textTransform: "uppercase",
                                  letterSpacing: "0.05em",
                                }}
                              >
                                Nova
                              </span>
                            )}
                            <div
                              style={{
                                fontSize: 11,
                                color: C.text4,
                                flexShrink: 0,
                              }}
                            >
                              {n.quando}
                            </div>
                          </div>
                          <div
                            style={{
                              fontSize: 12,
                              color: C.text3,
                              lineHeight: 1.6,
                              marginBottom: 10,
                            }}
                          >
                            {n.corpo}
                          </div>
                          {isEvaPlano && (
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                criarPlanoSugerido(n);
                                setNotifModalOpen(false);
                              }}
                              style={{
                                marginBottom: 10,
                                display: "inline-flex",
                                alignItems: "center",
                                gap: 6,
                                background: cfg.cor,
                                color: "#FFFFFF",
                                border: "none",
                                borderRadius: 9,
                                padding: "8px 14px",
                                fontSize: 11,
                                fontWeight: 700,
                                cursor: "pointer",
                                fontFamily: "inherit",
                                boxShadow: `0 2px 8px ${cfg.cor}40`,
                              }}
                            >
                              <Sparkles size={12} /> Criar plano de ação
                              automaticamente
                            </button>
                          )}
                          {/* Barra de feedback inline (modal) */}
                          <div
                            style={{
                              display: "flex",
                              alignItems: "center",
                              gap: 6,
                              paddingTop: 10,
                              borderTop: `1px dashed ${C.border}`,
                            }}
                          >
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                setNotifFeedback(n.id, "util");
                              }}
                              style={{
                                display: "flex",
                                alignItems: "center",
                                gap: 5,
                                background:
                                  n.feedback === "util"
                                    ? C.greenBg
                                    : "transparent",
                                border: `1px solid ${n.feedback === "util" ? C.g700 : C.border}`,
                                borderRadius: 7,
                                padding: "5px 11px",
                                cursor: "pointer",
                                fontSize: 11,
                                fontWeight: 600,
                                color: n.feedback === "util" ? C.g700 : C.text4,
                                fontFamily: "inherit",
                                transition: "all .15s",
                              }}
                            >
                              <ThumbsUp size={12} /> Útil
                            </button>
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                setNotifFeedback(n.id, "naoGostei");
                              }}
                              style={{
                                display: "flex",
                                alignItems: "center",
                                gap: 5,
                                background:
                                  n.feedback === "naoGostei"
                                    ? "#FEE2E2"
                                    : "transparent",
                                border: `1px solid ${n.feedback === "naoGostei" ? C.red : C.border}`,
                                borderRadius: 7,
                                padding: "5px 11px",
                                cursor: "pointer",
                                fontSize: 11,
                                fontWeight: 600,
                                color:
                                  n.feedback === "naoGostei" ? C.red : C.text4,
                                fontFamily: "inherit",
                                transition: "all .15s",
                              }}
                            >
                              <ThumbsDown size={12} /> Não gostei
                            </button>
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                ocultarNotif(n.id);
                              }}
                              style={{
                                display: "flex",
                                alignItems: "center",
                                gap: 5,
                                background: "transparent",
                                border: `1px solid ${C.border}`,
                                borderRadius: 7,
                                padding: "5px 11px",
                                cursor: "pointer",
                                fontSize: 11,
                                fontWeight: 600,
                                color: C.text4,
                                fontFamily: "inherit",
                                marginLeft: "auto",
                                transition: "all .15s",
                              }}
                            >
                              <EyeOff size={12} /> Ocultar
                            </button>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          );
        })()}
      {/* Modal de Plano de Ação — global, abre de qualquer aba (Jornada CTA, Acoes botão, etc) */}
      {modal && modal !== "pdf" && (
        <AcaoModal
          acao={modal === "new" ? null : modal}
          prefilled={modal === "new" ? prefilledAcao : null}
          onSave={(a) => {
            saveAcao(a);
            setPrefilledAcao(null);
          }}
          onClose={() => {
            setModal(null);
            setPrefilledAcao(null);
          }}
        />
      )}

      {/* Modal de Detalhes do Sucesso do Plano de Ação */}
      {sucessoModalOpen &&
        (() => {
          // Filtra ações com sucesso registrado
          const todasComSucesso = acoes.filter((a) => a.sucesso);

          // Aplica filtro de período baseado em data registradoEm
          const hoje = new Date();
          const filtroEm = (a) => {
            if (sucessoFiltroPeriodo === "todos") return true;
            const dataReg = a.sucesso?.registradoEm
              ? new Date(a.sucesso.registradoEm)
              : null;
            if (!dataReg) return sucessoFiltroPeriodo === "todos";
            const diffMeses = (hoje - dataReg) / (1000 * 60 * 60 * 24 * 30);
            if (sucessoFiltroPeriodo === "3m") return diffMeses <= 3;
            if (sucessoFiltroPeriodo === "6m") return diffMeses <= 6;
            if (sucessoFiltroPeriodo === "12m") return diffMeses <= 12;
            return true;
          };

          const sucessosFiltrados = todasComSucesso.filter(filtroEm);
          const totalPpFiltrado = sucessosFiltrados.reduce(
            (s, a) => s + safeGanhoPp(a.sucesso.ganhoPp),
            0,
          );
          const numFiltrado = sucessosFiltrados.length;
          const maiorIndividual = sucessosFiltrados.reduce(
            (m, a) => Math.max(m, safeGanhoPp(a.sucesso.ganhoPp)),
            0,
          );

          // Breakdown por etapa: agrupa por a.etapa
          const porEtapa = {};
          sucessosFiltrados.forEach((a) => {
            const et = a.etapa || "Outras";
            if (!porEtapa[et])
              porEtapa[et] = { etapa: et, count: 0, totalPp: 0, acoes: [] };
            porEtapa[et].count += 1;
            porEtapa[et].totalPp += safeGanhoPp(a.sucesso.ganhoPp);
            porEtapa[et].acoes.push(a);
          });
          const breakdownEtapa = Object.values(porEtapa).sort(
            (a, b) => b.totalPp - a.totalPp,
          );

          // Evolução cumulativa: ordena por data e acumula
          const ordenadosPorData = [...sucessosFiltrados]
            .filter((a) => a.sucesso?.registradoEm)
            .sort(
              (a, b) =>
                new Date(a.sucesso.registradoEm) -
                new Date(b.sucesso.registradoEm),
            );
          let acumulado = 0;
          const evolucaoData = ordenadosPorData.map((a) => {
            acumulado += safeGanhoPp(a.sucesso.ganhoPp);
            const d = new Date(a.sucesso.registradoEm);
            return {
              data: `${String(d.getDate()).padStart(2, "0")}/${String(d.getMonth() + 1).padStart(2, "0")}`,
              dataFull: a.sucesso.registradoEm,
              acumulado,
              ganho: safeGanhoPp(a.sucesso.ganhoPp),
              titulo: a.titulo,
              etapa: a.etapa,
            };
          });

          const PERIODO_OPTS = [
            { v: "todos", label: "Todo histórico" },
            { v: "12m", label: "Últimos 12 meses" },
            { v: "6m", label: "Últimos 6 meses" },
            { v: "3m", label: "Últimos 3 meses" },
          ];

          return (
            <div
              onClick={() => setSucessoModalOpen(false)}
              style={{
                position: "fixed",
                inset: 0,
                background: "rgba(0,0,0,.55)",
                backdropFilter: "blur(6px)",
                zIndex: 1000,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                padding: 20,
              }}
            >
              <div
                onClick={(e) => e.stopPropagation()}
                style={{
                  background: C.surface,
                  borderRadius: 18,
                  maxWidth: 920,
                  width: "100%",
                  maxHeight: "90vh",
                  display: "flex",
                  flexDirection: "column",
                  boxShadow: "0 24px 64px rgba(0,0,0,.3)",
                  overflow: "hidden",
                }}
              >
                {/* Header */}
                <div
                  style={{
                    padding: "18px 22px",
                    borderBottom: `1px solid ${C.border}`,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    background: `linear-gradient(135deg,${C.greenBg} 0%,${C.surface} 100%)`,
                    flexShrink: 0,
                  }}
                >
                  <div
                    style={{ display: "flex", alignItems: "center", gap: 12 }}
                  >
                    <div
                      style={{
                        width: 42,
                        height: 42,
                        background: C.green,
                        borderRadius: 12,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                      }}
                    >
                      <Trophy size={20} color="#FFFFFF" />
                    </div>
                    <div>
                      <h3
                        style={{
                          margin: 0,
                          fontSize: 16,
                          fontWeight: 700,
                          color: C.text,
                          letterSpacing: "-0.02em",
                        }}
                      >
                        Sucessos do plano de ação
                      </h3>
                      <div
                        style={{ fontSize: 11, color: C.text4, marginTop: 2 }}
                      >
                        Evolução cumulativa, breakdown por etapa, histórico
                        completo
                      </div>
                    </div>
                  </div>
                  <button
                    onClick={() => setSucessoModalOpen(false)}
                    style={{
                      background: "transparent",
                      border: "none",
                      cursor: "pointer",
                      padding: 6,
                      borderRadius: 6,
                      color: C.text4,
                      display: "flex",
                    }}
                  >
                    <XCircle size={22} />
                  </button>
                </div>

                {/* Filtros de período */}
                <div
                  style={{
                    padding: "14px 22px",
                    borderBottom: `1px solid ${C.border}`,
                    background: C.surf3,
                    display: "flex",
                    alignItems: "center",
                    gap: 10,
                    flexShrink: 0,
                  }}
                >
                  <span
                    style={{
                      fontSize: 10,
                      fontWeight: 700,
                      color: C.text3,
                      textTransform: "uppercase",
                      letterSpacing: "0.05em",
                    }}
                  >
                    Período
                  </span>
                  <div
                    style={{
                      display: "flex",
                      gap: 4,
                      background: C.surface,
                      borderRadius: 9,
                      padding: 3,
                      border: `1px solid ${C.border}`,
                    }}
                  >
                    {PERIODO_OPTS.map((opt) => (
                      <button
                        key={opt.v}
                        onClick={() => setSucessoFiltroPeriodo(opt.v)}
                        style={{
                          background:
                            sucessoFiltroPeriodo === opt.v
                              ? C.g700
                              : "transparent",
                          color:
                            sucessoFiltroPeriodo === opt.v
                              ? "#FFFFFF"
                              : C.text2,
                          border: "none",
                          borderRadius: 6,
                          padding: "5px 11px",
                          fontSize: 11,
                          fontWeight: 600,
                          cursor: "pointer",
                          fontFamily: "inherit",
                          transition: "all .15s",
                        }}
                      >
                        {opt.label}
                      </button>
                    ))}
                  </div>
                  <div style={{ flex: 1 }} />
                  <div style={{ fontSize: 10, color: C.text4 }}>
                    Mostrando <b style={{ color: C.text2 }}>{numFiltrado}</b>{" "}
                    {numFiltrado === 1 ? "sucesso" : "sucessos"}
                  </div>
                </div>

                {/* Body scroll */}
                <div
                  style={{ flex: 1, overflowY: "auto", padding: "18px 22px" }}
                >
                  {/* KPIs */}
                  <div style={{ display: "flex", gap: 10, marginBottom: 18 }}>
                    <div
                      style={{
                        flex: 1,
                        padding: "14px 16px",
                        background: C.greenBg,
                        borderRadius: 10,
                        border: `1px solid ${C.green}30`,
                      }}
                    >
                      <div
                        style={{
                          fontSize: 9,
                          color: C.green,
                          fontWeight: 700,
                          textTransform: "uppercase",
                          letterSpacing: "0.05em",
                          marginBottom: 4,
                        }}
                      >
                        Impacto cumulado
                      </div>
                      <div
                        style={{
                          fontSize: 26,
                          fontWeight: 700,
                          color: C.green,
                          letterSpacing: "-0.03em",
                          lineHeight: 1,
                        }}
                      >
                        +{totalPpFiltrado}pp
                      </div>
                    </div>
                    <div
                      style={{
                        flex: 1,
                        padding: "14px 16px",
                        background: C.surf3,
                        borderRadius: 10,
                        border: `1px solid ${C.border}`,
                      }}
                    >
                      <div
                        style={{
                          fontSize: 9,
                          color: C.text3,
                          fontWeight: 700,
                          textTransform: "uppercase",
                          letterSpacing: "0.05em",
                          marginBottom: 4,
                        }}
                      >
                        Ações com sucesso
                      </div>
                      <div
                        style={{
                          fontSize: 26,
                          fontWeight: 700,
                          color: C.text,
                          letterSpacing: "-0.03em",
                          lineHeight: 1,
                        }}
                      >
                        {numFiltrado}
                      </div>
                    </div>
                    <div
                      style={{
                        flex: 1,
                        padding: "14px 16px",
                        background: C.surf3,
                        borderRadius: 10,
                        border: `1px solid ${C.border}`,
                      }}
                    >
                      <div
                        style={{
                          fontSize: 9,
                          color: C.text3,
                          fontWeight: 700,
                          textTransform: "uppercase",
                          letterSpacing: "0.05em",
                          marginBottom: 4,
                        }}
                      >
                        Maior ganho individual
                      </div>
                      <div
                        style={{
                          fontSize: 26,
                          fontWeight: 700,
                          color: C.text,
                          letterSpacing: "-0.03em",
                          lineHeight: 1,
                        }}
                      >
                        +{maiorIndividual}pp
                      </div>
                    </div>
                    <div
                      style={{
                        flex: 1,
                        padding: "14px 16px",
                        background: C.surf3,
                        borderRadius: 10,
                        border: `1px solid ${C.border}`,
                      }}
                    >
                      <div
                        style={{
                          fontSize: 9,
                          color: C.text3,
                          fontWeight: 700,
                          textTransform: "uppercase",
                          letterSpacing: "0.05em",
                          marginBottom: 4,
                        }}
                      >
                        Etapas impactadas
                      </div>
                      <div
                        style={{
                          fontSize: 26,
                          fontWeight: 700,
                          color: C.text,
                          letterSpacing: "-0.03em",
                          lineHeight: 1,
                        }}
                      >
                        {breakdownEtapa.length}
                      </div>
                    </div>
                  </div>

                  {/* Gráfico de evolução cumulativa */}
                  {evolucaoData.length > 0 ? (
                    <Section title="Evolução cumulativa do impacto">
                      <div style={{ padding: "4px 6px" }}>
                        <ResponsiveContainer width="100%" height={240}>
                          <LineChart
                            data={evolucaoData}
                            margin={{ top: 10, right: 20, left: 0, bottom: 0 }}
                          >
                            <CartesianGrid
                              strokeDasharray="3 3"
                              stroke={C.border}
                              vertical={false}
                            />
                            <XAxis
                              dataKey="data"
                              tick={{ fontSize: 11, fill: C.text3 }}
                            />
                            <YAxis
                              tick={{ fontSize: 10, fill: C.text4 }}
                              label={{
                                value: "pp acumulados",
                                angle: -90,
                                position: "insideLeft",
                                fill: C.text4,
                                fontSize: 10,
                              }}
                            />
                            <Tooltip
                              contentStyle={{
                                background: C.surface,
                                border: `1px solid ${C.border}`,
                                borderRadius: 8,
                                fontSize: 11,
                              }}
                              formatter={(value, name, props) => [
                                `+${value}pp`,
                                "Acumulado",
                              ]}
                              labelFormatter={(label, payload) => {
                                if (payload && payload[0]) {
                                  const p = payload[0].payload;
                                  return `${label} — ${p.titulo} (+${p.ganho}pp em ${p.etapa})`;
                                }
                                return label;
                              }}
                            />
                            <Line
                              type="monotone"
                              dataKey="acumulado"
                              stroke={C.green}
                              strokeWidth={3}
                              dot={{ r: 5, fill: C.green, strokeWidth: 0 }}
                              activeDot={{ r: 7 }}
                            />
                          </LineChart>
                        </ResponsiveContainer>
                      </div>
                    </Section>
                  ) : (
                    <div
                      style={{
                        padding: "30px",
                        textAlign: "center",
                        color: C.text4,
                        fontSize: 12,
                        fontStyle: "italic",
                        background: C.surf3,
                        borderRadius: 10,
                      }}
                    >
                      Sem sucessos registrados no período selecionado.
                    </div>
                  )}

                  {/* Breakdown por etapa */}
                  {breakdownEtapa.length > 0 && (
                    <div style={{ marginTop: 18 }}>
                      <Section title="Sucessos por etapa da jornada">
                        <div
                          style={{
                            display: "flex",
                            flexDirection: "column",
                            gap: 8,
                          }}
                        >
                          {breakdownEtapa.map((b, i) => {
                            const pctMax = breakdownEtapa[0].totalPp;
                            const widthPct =
                              pctMax > 0
                                ? Math.round((b.totalPp / pctMax) * 100)
                                : 0;
                            return (
                              <div
                                key={b.etapa}
                                style={{
                                  padding: "11px 14px",
                                  background: C.surf3,
                                  borderRadius: 9,
                                  border: `1px solid ${C.border}`,
                                }}
                              >
                                <div
                                  style={{
                                    display: "flex",
                                    justifyContent: "space-between",
                                    alignItems: "center",
                                    marginBottom: 6,
                                  }}
                                >
                                  <div
                                    style={{
                                      fontSize: 12,
                                      fontWeight: 700,
                                      color: C.text,
                                    }}
                                  >
                                    {b.etapa}
                                  </div>
                                  <div
                                    style={{
                                      display: "flex",
                                      gap: 10,
                                      alignItems: "center",
                                    }}
                                  >
                                    <span
                                      style={{ fontSize: 10, color: C.text4 }}
                                    >
                                      {b.count}{" "}
                                      {b.count === 1 ? "ação" : "ações"}
                                    </span>
                                    <span
                                      style={{
                                        fontSize: 14,
                                        fontWeight: 700,
                                        color: C.green,
                                        letterSpacing: "-0.02em",
                                      }}
                                    >
                                      +{b.totalPp}pp
                                    </span>
                                  </div>
                                </div>
                                {/* Barra de proporção */}
                                <div
                                  style={{
                                    height: 5,
                                    background: C.surface,
                                    borderRadius: 3,
                                    overflow: "hidden",
                                  }}
                                >
                                  <div
                                    style={{
                                      height: "100%",
                                      width: `${widthPct}%`,
                                      background: `linear-gradient(90deg,${C.green},${C.g700})`,
                                      borderRadius: 3,
                                      transition: "width .5s",
                                    }}
                                  />
                                </div>
                                {/* Lista das ações */}
                                <div
                                  style={{
                                    marginTop: 8,
                                    display: "flex",
                                    flexDirection: "column",
                                    gap: 3,
                                  }}
                                >
                                  {b.acoes.slice(0, 3).map((a, j) => (
                                    <div
                                      key={j}
                                      style={{
                                        fontSize: 10,
                                        color: C.text3,
                                        display: "flex",
                                        justifyContent: "space-between",
                                        gap: 8,
                                      }}
                                    >
                                      <span
                                        style={{
                                          flex: 1,
                                          minWidth: 0,
                                          overflow: "hidden",
                                          textOverflow: "ellipsis",
                                          whiteSpace: "nowrap",
                                        }}
                                      >
                                        {a.titulo}
                                      </span>
                                      <span
                                        style={{
                                          color: C.green,
                                          fontWeight: 600,
                                          flexShrink: 0,
                                        }}
                                      >
                                        +{safeGanhoPp(a.sucesso.ganhoPp)}pp
                                      </span>
                                    </div>
                                  ))}
                                  {b.acoes.length > 3 && (
                                    <div
                                      style={{
                                        fontSize: 10,
                                        color: C.text4,
                                        fontStyle: "italic",
                                      }}
                                    >
                                      +{b.acoes.length - 3} ações adicionais
                                    </div>
                                  )}
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      </Section>
                    </div>
                  )}
                </div>

                {/* Footer */}
                <div
                  style={{
                    padding: "12px 22px",
                    borderTop: `1px solid ${C.border}`,
                    background: C.surf3,
                    fontSize: 10,
                    color: C.text4,
                    fontStyle: "italic",
                    lineHeight: 1.5,
                    flexShrink: 0,
                  }}
                >
                  ⓘ O impacto cumulado soma os ganhos em pontos percentuais (pp)
                  das ações concluídas com sucesso registrado. Use os filtros
                  para isolar períodos específicos e identificar quais etapas da
                  jornada concentram seus melhores resultados.
                </div>
              </div>
            </div>
          );
        })()}

      {/* Modal de Verbatins — todas as frases da etapa selecionada */}
      {verbatinsModal &&
        (() => {
          const verbatinsAll = VERBATINS_POR_ETAPA[verbatinsModal.etapa] || [];
          const reclamacoes = verbatinsAll.filter((v) => v.tipo === "detrator");
          const elogios = verbatinsAll.filter((v) => v.tipo === "promotor");
          const neutros = verbatinsAll.filter((v) => v.tipo === "neutro");
          // Driver labels que aparecem
          const driversUnicos = [...new Set(verbatinsAll.map((v) => v.driver))];

          return (
            <div
              onClick={() => setVerbatinsModal(null)}
              style={{
                position: "fixed",
                inset: 0,
                background: "rgba(0,0,0,.55)",
                backdropFilter: "blur(6px)",
                zIndex: 1000,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                padding: 20,
              }}
            >
              <div
                onClick={(e) => e.stopPropagation()}
                style={{
                  background: C.surface,
                  borderRadius: 18,
                  maxWidth: 760,
                  width: "100%",
                  maxHeight: "90vh",
                  display: "flex",
                  flexDirection: "column",
                  boxShadow: "0 24px 64px rgba(0,0,0,.3)",
                  overflow: "hidden",
                }}
              >
                {/* Header */}
                <div
                  style={{
                    padding: "18px 22px",
                    borderBottom: `1px solid ${C.border}`,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    background: `linear-gradient(135deg,${C.p100 || "#EDE9FE"} 0%,${C.surface} 100%)`,
                    flexShrink: 0,
                  }}
                >
                  <div
                    style={{ display: "flex", alignItems: "center", gap: 12 }}
                  >
                    <div
                      style={{
                        width: 38,
                        height: 38,
                        background: C.p500,
                        borderRadius: 11,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                      }}
                    >
                      <MessageSquare size={18} color="#FFFFFF" />
                    </div>
                    <div>
                      <h3
                        style={{
                          margin: 0,
                          fontSize: 16,
                          fontWeight: 700,
                          color: C.text,
                          letterSpacing: "-0.02em",
                        }}
                      >
                        O que os clientes dizem sobre{" "}
                        <span style={{ color: C.p500 }}>
                          {verbatinsModal.etapa}
                        </span>
                      </h3>
                      <div
                        style={{
                          fontSize: 11,
                          color: C.text4,
                          marginTop: 2,
                          display: "flex",
                          gap: 10,
                        }}
                      >
                        <span>
                          <b style={{ color: C.red }}>{reclamacoes.length}</b>{" "}
                          reclamações
                        </span>
                        <span>
                          <b style={{ color: C.green }}>{elogios.length}</b>{" "}
                          elogios
                        </span>
                        {neutros.length > 0 && (
                          <span>
                            <b style={{ color: C.orange }}>{neutros.length}</b>{" "}
                            neutros
                          </span>
                        )}
                        <span style={{ color: C.text4 }}>·</span>
                        <span>{driversUnicos.length} drivers cobertos</span>
                      </div>
                    </div>
                  </div>
                  <button
                    onClick={() => setVerbatinsModal(null)}
                    style={{
                      background: "transparent",
                      border: "none",
                      cursor: "pointer",
                      padding: 6,
                      borderRadius: 6,
                      color: C.text4,
                      display: "flex",
                    }}
                  >
                    <XCircle size={22} />
                  </button>
                </div>

                {/* Lista scrollável */}
                <div
                  style={{ flex: 1, overflowY: "auto", padding: "18px 22px" }}
                >
                  {/* Reclamações */}
                  {reclamacoes.length > 0 && (
                    <div style={{ marginBottom: 18 }}>
                      <div
                        style={{
                          fontSize: 11,
                          fontWeight: 700,
                          color: C.red,
                          textTransform: "uppercase",
                          letterSpacing: "0.05em",
                          marginBottom: 8,
                          display: "flex",
                          alignItems: "center",
                          gap: 6,
                        }}
                      >
                        <ArrowDownRight size={13} /> Reclamações (
                        {reclamacoes.length}) — o que mais atrapalha
                      </div>
                      <div
                        style={{
                          display: "flex",
                          flexDirection: "column",
                          gap: 8,
                        }}
                      >
                        {reclamacoes.map((v, i) => (
                          <div
                            key={i}
                            style={{
                              padding: "11px 14px",
                              background: C.surf3,
                              borderRadius: 9,
                              borderLeft: `3px solid ${C.red}`,
                            }}
                          >
                            <div
                              style={{
                                fontSize: 9,
                                fontWeight: 700,
                                color: C.red,
                                textTransform: "uppercase",
                                letterSpacing: "0.04em",
                                marginBottom: 5,
                                display: "flex",
                                alignItems: "center",
                                gap: 4,
                              }}
                            >
                              <ArrowDownRight size={10} /> {v.driver}
                            </div>
                            <div
                              style={{
                                fontSize: 13,
                                color: C.text2,
                                lineHeight: 1.5,
                                fontStyle: "italic",
                              }}
                            >
                              "{v.texto}"
                            </div>
                            <div
                              style={{
                                fontSize: 10,
                                color: C.text4,
                                marginTop: 6,
                                display: "flex",
                                flexWrap: "wrap",
                                gap: 6,
                                alignItems: "center",
                              }}
                            >
                              <span style={{ fontWeight: 600, color: C.text3 }}>
                                {v.genero === "homem" ? "Homem" : "Mulher"}
                              </span>
                              <span>·</span>
                              <span>{v.idade}a</span>
                              <span>·</span>
                              <span>
                                {v.idade <= 29
                                  ? "Gen Z"
                                  : v.idade <= 45
                                    ? "Millennial"
                                    : v.idade <= 61
                                      ? "Gen X"
                                      : v.idade <= 80
                                        ? "Boomer"
                                        : "Silenciosa"}
                              </span>
                              <span>·</span>
                              <span>{v.cidade}</span>
                              <span
                                style={{ marginLeft: "auto", color: C.text4 }}
                              >
                                {v.data}
                              </span>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Elogios */}
                  {elogios.length > 0 && (
                    <div style={{ marginBottom: neutros.length > 0 ? 18 : 0 }}>
                      <div
                        style={{
                          fontSize: 11,
                          fontWeight: 700,
                          color: C.green,
                          textTransform: "uppercase",
                          letterSpacing: "0.05em",
                          marginBottom: 8,
                          display: "flex",
                          alignItems: "center",
                          gap: 6,
                        }}
                      >
                        <ArrowUpRight size={13} /> Elogios ({elogios.length}) —
                        o que mais ajuda
                      </div>
                      <div
                        style={{
                          display: "flex",
                          flexDirection: "column",
                          gap: 8,
                        }}
                      >
                        {elogios.map((v, i) => (
                          <div
                            key={i}
                            style={{
                              padding: "11px 14px",
                              background: C.surf3,
                              borderRadius: 9,
                              borderLeft: `3px solid ${C.green}`,
                            }}
                          >
                            <div
                              style={{
                                fontSize: 9,
                                fontWeight: 700,
                                color: C.green,
                                textTransform: "uppercase",
                                letterSpacing: "0.04em",
                                marginBottom: 5,
                                display: "flex",
                                alignItems: "center",
                                gap: 4,
                              }}
                            >
                              <ArrowUpRight size={10} /> {v.driver}
                            </div>
                            <div
                              style={{
                                fontSize: 13,
                                color: C.text2,
                                lineHeight: 1.5,
                                fontStyle: "italic",
                              }}
                            >
                              "{v.texto}"
                            </div>
                            <div
                              style={{
                                fontSize: 10,
                                color: C.text4,
                                marginTop: 6,
                                display: "flex",
                                flexWrap: "wrap",
                                gap: 6,
                                alignItems: "center",
                              }}
                            >
                              <span style={{ fontWeight: 600, color: C.text3 }}>
                                {v.genero === "homem" ? "Homem" : "Mulher"}
                              </span>
                              <span>·</span>
                              <span>{v.idade}a</span>
                              <span>·</span>
                              <span>
                                {v.idade <= 29
                                  ? "Gen Z"
                                  : v.idade <= 45
                                    ? "Millennial"
                                    : v.idade <= 61
                                      ? "Gen X"
                                      : v.idade <= 80
                                        ? "Boomer"
                                        : "Silenciosa"}
                              </span>
                              <span>·</span>
                              <span>{v.cidade}</span>
                              <span
                                style={{ marginLeft: "auto", color: C.text4 }}
                              >
                                {v.data}
                              </span>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Neutros */}
                  {neutros.length > 0 && (
                    <div>
                      <div
                        style={{
                          fontSize: 11,
                          fontWeight: 700,
                          color: C.orange,
                          textTransform: "uppercase",
                          letterSpacing: "0.05em",
                          marginBottom: 8,
                          display: "flex",
                          alignItems: "center",
                          gap: 6,
                        }}
                      >
                        Neutros ({neutros.length}) — observações sem polaridade
                        clara
                      </div>
                      <div
                        style={{
                          display: "flex",
                          flexDirection: "column",
                          gap: 8,
                        }}
                      >
                        {neutros.map((v, i) => (
                          <div
                            key={i}
                            style={{
                              padding: "11px 14px",
                              background: C.surf3,
                              borderRadius: 9,
                              borderLeft: `3px solid ${C.orange}`,
                            }}
                          >
                            <div
                              style={{
                                fontSize: 9,
                                fontWeight: 700,
                                color: C.orange,
                                textTransform: "uppercase",
                                letterSpacing: "0.04em",
                                marginBottom: 5,
                              }}
                            >
                              {v.driver}
                            </div>
                            <div
                              style={{
                                fontSize: 13,
                                color: C.text2,
                                lineHeight: 1.5,
                                fontStyle: "italic",
                              }}
                            >
                              "{v.texto}"
                            </div>
                            <div
                              style={{
                                fontSize: 10,
                                color: C.text4,
                                marginTop: 6,
                                display: "flex",
                                flexWrap: "wrap",
                                gap: 6,
                                alignItems: "center",
                              }}
                            >
                              <span style={{ fontWeight: 600, color: C.text3 }}>
                                {v.genero === "homem" ? "Homem" : "Mulher"}
                              </span>
                              <span>·</span>
                              <span>{v.idade}a</span>
                              <span>·</span>
                              <span>
                                {v.idade <= 29
                                  ? "Gen Z"
                                  : v.idade <= 45
                                    ? "Millennial"
                                    : v.idade <= 61
                                      ? "Gen X"
                                      : v.idade <= 80
                                        ? "Boomer"
                                        : "Silenciosa"}
                              </span>
                              <span>·</span>
                              <span>{v.cidade}</span>
                              <span
                                style={{ marginLeft: "auto", color: C.text4 }}
                              >
                                {v.data}
                              </span>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {verbatinsAll.length === 0 && (
                    <div
                      style={{
                        padding: "30px 20px",
                        textAlign: "center",
                        color: C.text4,
                        fontSize: 13,
                        fontStyle: "italic",
                      }}
                    >
                      Sem verbatins coletados nesta etapa no período
                      selecionado.
                    </div>
                  )}
                </div>

                {/* Footer com nota */}
                <div
                  style={{
                    padding: "12px 22px",
                    borderTop: `1px solid ${C.border}`,
                    background: C.surf3,
                    fontSize: 10,
                    color: C.text4,
                    fontStyle: "italic",
                    lineHeight: 1.5,
                    flexShrink: 0,
                  }}
                >
                  ⓘ Pesquisa anônima · Verbatins extraídos automaticamente das
                  respostas abertas pelo Eva Engine, classificados por driver
                  via NLP
                </div>
              </div>
            </div>
          );
        })()}

      {/* Modal de engajamento amostral — abre pelo badge no header */}
      {engajamentoModalOpen && (
        <div
          onClick={() => setEngajamentoModalOpen(false)}
          style={{
            position: "fixed",
            inset: 0,
            background: "rgba(0,0,0,.55)",
            backdropFilter: "blur(6px)",
            zIndex: 1000,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            padding: 20,
          }}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              background: C.surface,
              borderRadius: 18,
              maxWidth: 780,
              width: "100%",
              maxHeight: "90vh",
              overflow: "auto",
              boxShadow: "0 24px 64px rgba(0,0,0,.3)",
            }}
          >
            {/* Header */}
            <div
              style={{
                padding: "18px 22px",
                borderBottom: `1px solid ${C.border}`,
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                background: `linear-gradient(135deg,${C.greenBg} 0%,${C.surface} 100%)`,
              }}
            >
              <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                <div
                  style={{
                    width: 36,
                    height: 36,
                    background: C.g700,
                    borderRadius: 10,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <Activity size={17} color="#FFFFFF" />
                </div>
                <div>
                  <div
                    style={{
                      fontSize: 15,
                      fontWeight: 700,
                      color: C.text,
                      letterSpacing: "-0.02em",
                    }}
                  >
                    Engajamento amostral
                  </div>
                  <div style={{ fontSize: 11, color: C.text4, marginTop: 2 }}>
                    Robustez estatística da pesquisa neste período
                  </div>
                </div>
              </div>
              <button
                onClick={() => setEngajamentoModalOpen(false)}
                title="Fechar"
                style={{
                  background: "transparent",
                  border: "none",
                  cursor: "pointer",
                  padding: 6,
                  borderRadius: 8,
                  color: C.text3,
                  fontFamily: "inherit",
                }}
              >
                <X size={18} />
              </button>
            </div>

            {/* Body */}
            <div style={{ padding: "20px 22px" }}>
              <div
                style={{
                  display: "flex",
                  gap: 10,
                  flexWrap: "wrap",
                  marginBottom: 18,
                }}
              >
                {[
                  {
                    l: "Respondentes",
                    v: de.eng.resp.toLocaleString(),
                    c: C.g700,
                    Icon: UserCheck,
                  },
                  {
                    l: "Ondas no período",
                    v: PER[pE].ondas,
                    c: C.p500,
                    Icon: ClipboardList,
                  },
                  {
                    l: "Margem de erro",
                    v: `±${moe(de.eng.resp)}%`,
                    c: de.eng.resp >= 400 ? C.green : C.orange,
                    Icon: Crosshair,
                  },
                  {
                    l: "Nível de confiança",
                    v: "95%",
                    c: C.g600,
                    Icon: CheckCircle2,
                  },
                ].map((k, i) => (
                  <Card
                    key={i}
                    style={{ flex: 1, minWidth: 130, padding: "14px 16px" }}
                  >
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: 6,
                        marginBottom: 6,
                      }}
                    >
                      <k.Icon size={13} color={k.c} />
                      <div
                        style={{
                          fontSize: 9,
                          color: C.text4,
                          fontWeight: 600,
                          textTransform: "uppercase",
                          letterSpacing: "0.04em",
                        }}
                      >
                        {k.l}
                      </div>
                    </div>
                    <div
                      style={{
                        fontSize: 22,
                        fontWeight: 700,
                        color: k.c,
                        letterSpacing: "-0.03em",
                        fontFamily: F.display,
                      }}
                    >
                      {k.v}
                    </div>
                  </Card>
                ))}
              </div>

              <Section title="Respondentes por mês" pad={false}>
                <div style={{ padding: "0 20px 16px" }}>
                  <ResponsiveContainer width="100%" height={200}>
                    <BarChart
                      data={de.eng.mensal}
                      margin={{ top: 8, right: 20 }}
                    >
                      <CartesianGrid
                        strokeDasharray="3 3"
                        stroke={C.border}
                        vertical={false}
                      />
                      <XAxis
                        dataKey="m"
                        tick={{ fontSize: 10, fill: C.text2 }}
                      />
                      <YAxis tick={{ fontSize: 9, fill: C.text4 }} />
                      <Tooltip content={<TT />} />
                      <Bar
                        dataKey="r"
                        name="Respondentes"
                        fill={C.g600}
                        radius={[4, 4, 0, 0]}
                      />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </Section>

              {/* Recomendação de granularidade — qual período usar como base de análise */}
              {(() => {
                const periodOrder = [
                  "mensal",
                  "trimestral",
                  "semestral",
                  "anual",
                ];
                const periodLabel = {
                  mensal: "Mensal",
                  trimestral: "Trimestral",
                  semestral: "Semestral",
                  anual: "Anual",
                };
                const periodDescr = {
                  mensal:
                    "Decisões em tempo real, ajustes táticos rápidos, acompanhamento de campanhas",
                  trimestral:
                    "Análise estratégica balanceada, comparação T vs T, ciclo de planos de ação",
                  semestral:
                    "Visão consolidada, relatórios executivos, planejamento de médio prazo",
                  anual:
                    "Visão de longo prazo, metas anuais, comparação ano vs ano",
                };
                // Encontra o mais granular com ME ≤ 3.5% (Excelente)
                const recomendadoExcelente = periodOrder.find(
                  (k) => moe(PER[k].resp) <= 3.5,
                );
                // Encontra o mais granular com ME ≤ 5% (Aceitável)
                const recomendadoAceitavel = periodOrder.find(
                  (k) => moe(PER[k].resp) <= 5,
                );
                const recKey =
                  recomendadoExcelente || recomendadoAceitavel || "anual";
                const isExcelente = !!recomendadoExcelente;
                const isAceitavel =
                  !recomendadoExcelente && !!recomendadoAceitavel;
                const recColor = isExcelente
                  ? C.green
                  : isAceitavel
                    ? C.orange
                    : C.red;
                const recBg = isExcelente
                  ? C.greenBg
                  : isAceitavel
                    ? C.orangeBg
                    : C.redBg;
                const recIconBg = isExcelente
                  ? C.green
                  : isAceitavel
                    ? C.orange
                    : C.red;
                const recRespostas = PER[recKey].resp.toLocaleString();
                const recMoe = moe(PER[recKey].resp);

                return (
                  <div style={{ marginTop: 14 }}>
                    <Section title="Recomendação de granularidade">
                      <div
                        style={{
                          fontSize: 11,
                          color: C.text3,
                          marginBottom: 12,
                          lineHeight: 1.6,
                        }}
                      >
                        Com base no volume de respondentes coletados, qual
                        período é estatisticamente confiável para análise:
                      </div>

                      {/* Big recommendation card */}
                      <div
                        style={{
                          padding: "16px 18px",
                          background: recBg,
                          border: `2px solid ${recColor}`,
                          borderRadius: 12,
                          marginBottom: 12,
                        }}
                      >
                        <div
                          style={{
                            display: "flex",
                            alignItems: "center",
                            gap: 14,
                          }}
                        >
                          <div
                            style={{
                              width: 44,
                              height: 44,
                              background: recIconBg,
                              borderRadius: 12,
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              flexShrink: 0,
                              boxShadow: `0 4px 12px ${recColor}40`,
                            }}
                          >
                            <CheckCircle2 size={22} color="#FFFFFF" />
                          </div>
                          <div style={{ flex: 1, minWidth: 0 }}>
                            <div
                              style={{
                                fontSize: 9,
                                fontWeight: 700,
                                color: recColor,
                                textTransform: "uppercase",
                                letterSpacing: "0.06em",
                                marginBottom: 2,
                              }}
                            >
                              USE COMO BASE DE ANÁLISE
                            </div>
                            <div
                              style={{
                                fontSize: 20,
                                fontWeight: 700,
                                color: C.text,
                                letterSpacing: "-0.02em",
                                lineHeight: 1.1,
                              }}
                            >
                              {periodLabel[recKey]}{" "}
                              <span
                                style={{
                                  fontSize: 13,
                                  fontWeight: 500,
                                  color: C.text3,
                                }}
                              >
                                · ±{recMoe}% margem de erro
                              </span>
                            </div>
                            <div
                              style={{
                                fontSize: 11,
                                color: C.text3,
                                marginTop: 4,
                                lineHeight: 1.4,
                              }}
                            >
                              {periodDescr[recKey]}
                            </div>
                          </div>
                        </div>
                      </div>

                      {/* Detalhamento por período */}
                      <div
                        style={{
                          display: "flex",
                          flexDirection: "column",
                          gap: 6,
                        }}
                      >
                        {periodOrder.map((k) => {
                          const v = PER[k];
                          const m = moe(v.resp);
                          const good = m <= 3.5;
                          const ok = m <= 5;
                          const isRec = k === recKey;
                          const status = good
                            ? "✓ Recomendado"
                            : ok
                              ? "△ Cautela: use com nota de margem ampla"
                              : "✗ Não use isoladamente — sample insuficiente";
                          const statusColor = good
                            ? C.green
                            : ok
                              ? C.orange
                              : C.red;

                          return (
                            <div
                              key={k}
                              style={{
                                display: "flex",
                                alignItems: "center",
                                gap: 10,
                                padding: "9px 12px",
                                background: isRec ? recBg : C.surf3,
                                border: isRec
                                  ? `1.5px solid ${recColor}`
                                  : `1px solid ${C.border}`,
                                borderRadius: 8,
                              }}
                            >
                              <div style={{ flex: 1, minWidth: 0 }}>
                                <div
                                  style={{
                                    fontSize: 11,
                                    fontWeight: 700,
                                    color: C.text,
                                    display: "flex",
                                    alignItems: "center",
                                    gap: 6,
                                  }}
                                >
                                  {periodLabel[k]}
                                  {isRec && (
                                    <Tag label="recomendado" color={recColor} />
                                  )}
                                </div>
                                <div
                                  style={{
                                    fontSize: 10,
                                    color: statusColor,
                                    marginTop: 2,
                                    fontWeight: 500,
                                  }}
                                >
                                  {status}
                                </div>
                              </div>
                              <div
                                style={{ textAlign: "right", flexShrink: 0 }}
                              >
                                <div
                                  style={{
                                    fontSize: 13,
                                    fontWeight: 700,
                                    color: statusColor,
                                    fontFamily: F.display,
                                    letterSpacing: "-0.02em",
                                  }}
                                >
                                  ±{m}%
                                </div>
                                <div style={{ fontSize: 9, color: C.text4 }}>
                                  {v.resp.toLocaleString()} resp.
                                </div>
                              </div>
                            </div>
                          );
                        })}
                      </div>

                      <div
                        style={{
                          marginTop: 12,
                          padding: "10px 12px",
                          background: C.surf3,
                          borderRadius: 8,
                          fontSize: 10,
                          color: C.text3,
                          lineHeight: 1.6,
                        }}
                      >
                        <b style={{ color: C.text }}>Como ler:</b> a
                        recomendação é baseada na granularidade mais detalhada
                        cuja amostra sustenta margem de erro confiável (≤3,5%).
                        Granularidades mais finas podem ser visualizadas no
                        dashboard, mas decisões críticas devem se basear no
                        período recomendado. Períodos marcados como "cautela"
                        podem ser usados como tendência indicativa, sempre
                        acompanhados da margem de erro explícita.
                      </div>
                    </Section>
                  </div>
                );
              })()}

              <div style={{ marginTop: 14 }}>
                <Section title="Margem de erro por período">
                  <div
                    style={{
                      fontSize: 11,
                      color: C.text3,
                      marginBottom: 10,
                      lineHeight: 1.6,
                    }}
                  >
                    Calculada com <b>95%</b> de confiança:&nbsp;
                    <b
                      style={{
                        color: C.text,
                        fontFamily: F.mono,
                        fontSize: 11,
                      }}
                    >
                      ME = 1,96 × √(0,25 / n) × 100
                    </b>
                  </div>
                  <div
                    style={{ display: "flex", flexDirection: "column", gap: 6 }}
                  >
                    {Object.entries(PER).map(([k, v]) => {
                      const me = moe(v.resp);
                      const good = me <= 3.5;
                      const ok = me <= 5;
                      return (
                        <div
                          key={k}
                          style={{
                            display: "flex",
                            alignItems: "center",
                            gap: 10,
                            padding: "7px 11px",
                            background: C.surf3,
                            borderRadius: 8,
                          }}
                        >
                          <div style={{ flex: 1 }}>
                            <div
                              style={{
                                fontSize: 11,
                                fontWeight: 600,
                                color: C.text,
                              }}
                            >
                              {v.short}
                            </div>
                            <div style={{ fontSize: 9, color: C.text4 }}>
                              {v.resp.toLocaleString()} respondentes
                            </div>
                          </div>
                          <div
                            style={{
                              fontSize: 16,
                              fontWeight: 700,
                              letterSpacing: "-0.03em",
                              color: good ? C.green : ok ? C.orange : C.red,
                              fontFamily: F.display,
                            }}
                          >
                            ±{me}%
                          </div>
                          <Tag
                            label={
                              good ? "Excelente" : ok ? "Aceitável" : "Atenção"
                            }
                            color={good ? C.green : ok ? C.orange : C.red}
                          />
                        </div>
                      );
                    })}
                  </div>
                  <div
                    style={{
                      marginTop: 8,
                      padding: "7px 10px",
                      background: C.blueBg,
                      borderRadius: 7,
                      fontSize: 10,
                      color: C.blue,
                    }}
                  >
                    ≤3,5% Excelente · ≤5% Aceitável · &gt;5% Atenção
                  </div>
                </Section>
              </div>
            </div>
          </div>
        </div>
      )}

      {modal === "pdf" && (
        <PDFExportModal
          currentPeriod={periodoG}
          onClose={() => setModal(null)}
          onGenerate={({ pages, periodo, customRange }) => {
            const pageLabels = {
              executivo: "Visão Geral",
              jornada: "Jornada",
              tendencias: "Tendências",
              segmentos: "Segmentação",
              engajamento: "Engajamento",
              drivers: "Drivers",
              benchmark: "Benchmarking",
              acoes: "Plano de Ação",
              coop: "CooperAção Unimed",
              quest: "Metodologia",
            };
            const periodoLabel =
              periodo === "custom"
                ? `${customRange.inicio} → ${customRange.fim}`
                : PER[periodo] && PER[periodo].label;
            alert(
              `📄 PDF será gerado com:\n\nPáginas (${pages.length}):\n  • ${pages.map((p) => pageLabels[p]).join("\n  • ")}\n\nPeríodo: ${periodoLabel}\n\n(Em produção, isso dispararia o backend de geração de PDF.)`,
            );
            setModal(null);
          }}
        />
      )}
      <style>{`@media print{.no-print{display:none!important;}}* { box-sizing:border-box; } button,input,select,textarea{font-family:inherit;} ::-webkit-scrollbar{width:5px;height:5px;} ::-webkit-scrollbar-track{background:transparent;} ::-webkit-scrollbar-thumb{background:${C.border};border-radius:3px;}`}</style>
    </div>
  );
}
